/**
 * BenchmarkTab.js — Human vs AI Performance Comparison
 * v1.5 — KB-grounded benchmark: RunTestModal before run, Groundedness metric in results.
 *
 * API endpoints:
 *   GET  /api/zendesk/tickets?limit=1000   — Load imported tickets
 *   POST /api/benchmark/single             — Benchmark one ticket by ID
 *   POST /api/benchmark/batch              — Benchmark selected tickets
 *   POST /api/benchmark/mock-run           — Demo mode (no Zendesk needed)
 *   GET  /api/benchmark/summary            — Load previous results
 *   GET  /api/benchmark/results            — Load individual results
 *   DELETE /api/benchmark/results          — Clear all results
 */

import { useApi } from './hooks/useApi';
import { useBilling } from './hooks/useBilling';
import React, { useState, useEffect, useMemo } from 'react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, AreaChart, Area, XAxis, YAxis, Cell, CartesianGrid,
  ResponsiveContainer, Tooltip, Legend
} from 'recharts';
import RunTestModal from './RunTestModal';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ── Outcome config ────────────────────────────────────────────────────────────
const OUTCOME_CONFIG = {
  resolved:         { label: 'Resolved',            color: '#10b981', bg: 'rgba(16,185,129,0.15)'  },
  escalated:        { label: 'Escalated',           color: '#f59e0b', bg: 'rgba(245,158,11,0.15)'  },
  waiting_internal: { label: 'Waiting on Internal', color: '#8b5cf6', bg: 'rgba(139,92,246,0.15)'  },
  waiting_customer: { label: 'Waiting on Customer', color: '#3b82f6', bg: 'rgba(59,130,246,0.15)'  },
  unresolved:       { label: 'Unresolved',          color: '#ef4444', bg: 'rgba(239,68,68,0.15)'   },
  no_response:      { label: 'No Agent Response',   color: '#64748b', bg: 'rgba(100,116,139,0.15)' },
};

const PRIORITY_COLOR = { urgent: '#ef4444', high: '#f59e0b', normal: '#10b981', low: '#3b82f6' };
const STATUS_COLOR   = { open: '#f59e0b', pending: '#3b82f6', solved: '#10b981', closed: '#64748b', new: '#a78bfa' };

// ── Tiny reusable components ──────────────────────────────────────────────────

const OutcomeBadge = ({ outcome }) => {
  const cfg = OUTCOME_CONFIG[outcome] || OUTCOME_CONFIG.unresolved;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.color}40`, whiteSpace: 'nowrap' }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.color }} />{cfg.label}
    </span>
  );
};

const ScoreRing = ({ score, color, size = 52 }) => {
  if (score === null || score === undefined) return (
    <div style={{ width: size, height: size, borderRadius: '50%', border: '3px dashed #334155', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontSize: 10 }}>N/A</div>
  );
  const r = (size / 2) - 5, circ = 2 * Math.PI * r, dash = (score / 100) * circ;
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#1e293b" strokeWidth={4} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={4} strokeDasharray={`${dash} ${circ - dash}`} strokeLinecap="round" />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: size * 0.22, fontWeight: 800, color }}>{score}</span>
        <span style={{ fontSize: size * 0.13, color: '#64748b' }}>/ 100</span>
      </div>
    </div>
  );
};

const MetricRow = ({ label, human, ai }) => {
  const diff = ai - human, winner = diff > 0.2 ? 'ai' : diff < -0.2 ? 'human' : 'tied';
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 1fr', gap: 8, alignItems: 'center', padding: '5px 0', borderBottom: '1px solid #1e293b20' }}>
      <div style={{ textAlign: 'right', fontSize: 13, fontWeight: 600, color: winner === 'human' ? '#38bdf8' : '#94a3b8' }}>{human.toFixed(1)}/5 {winner === 'human' && '◀'}</div>
      <div style={{ textAlign: 'center', fontSize: 11, color: '#64748b', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: winner === 'ai' ? '#a78bfa' : '#94a3b8' }}>{winner === 'ai' && '▶ '}{ai.toFixed(1)}/5</div>
    </div>
  );
};

const ChatBubble = ({ msg, side, cardBg, border, text: textColor }) => {
  const msgText = msg.content || msg.text || '';
  const isCustomer = msg.role === 'customer';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: isCustomer ? 'flex-start' : 'flex-end', marginBottom: 10 }}>
      <div style={{ fontSize: 10, color: '#64748b', marginBottom: 3 }}>{isCustomer ? '👤 Customer' : side === 'human' ? '🧑‍💼 Agent' : '🤖 AI Agent'}</div>
      <div style={{ maxWidth: '85%', padding: '10px 14px', borderRadius: isCustomer ? '4px 12px 12px 12px' : '12px 4px 12px 12px', background: isCustomer ? cardBg : side === 'human' ? 'rgba(56,189,248,0.1)' : 'rgba(139,92,246,0.1)', border: `1px solid ${isCustomer ? border : side === 'human' ? 'rgba(56,189,248,0.3)' : 'rgba(139,92,246,0.3)'}`, fontSize: 13, color: textColor, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{msgText}</div>
    </div>
  );
};

// ── Ticket selection card (used in the browse/select phase) ───────────────────

const TicketSelectCard = ({ ticket, ticketId, selected, onToggle, result, border, text, muted, cardBg }) => {
  const [expanded, setExpanded] = useState(false);
  const tags = ticket.tags || [];
  const hasResult = !!result;
  const winner = result?.comparison?.winner;

  return (
    <div onClick={() => onToggle(ticketId)}
      style={{
        padding: '14px 16px', borderRadius: 10, cursor: 'pointer',
        border: `2px solid ${selected ? '#7c3aed' : hasResult ? (winner === 'ai' ? 'rgba(167,139,250,0.3)' : winner === 'human' ? 'rgba(56,189,248,0.3)' : border) : border}`,
        background: selected ? 'rgba(124,58,237,0.08)' : cardBg,
        transition: 'border-color 0.15s',
      }}>

      {/* Main row */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      {/* Checkbox */}
      <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${selected ? '#7c3aed' : border}`, background: selected ? '#7c3aed' : 'transparent', flexShrink: 0, marginTop: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {selected && <span style={{ color: 'white', fontSize: 11, fontWeight: 900 }}>✓</span>}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
          <span style={{ fontSize: 11, color: '#64748b', fontFamily: 'monospace' }}>#{ticket.zendesk_ticket_id || ticket.id}</span>
          {ticket.priority && <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: PRIORITY_COLOR[ticket.priority] || '#94a3b8' }}>{ticket.priority}</span>}
          {ticket.status   && <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10, background: `${STATUS_COLOR[ticket.status]}20`, color: STATUS_COLOR[ticket.status] || '#94a3b8', border: `1px solid ${STATUS_COLOR[ticket.status]}40` }}>{ticket.status}</span>}
          {ticket.channel  && <span style={{ fontSize: 10, color: '#64748b' }}>📡 {ticket.channel}</span>}
          {ticket.comments?.length > 0 && <span style={{ fontSize: 10, color: '#10b981' }}>💬 {ticket.comments.length} comment{ticket.comments.length !== 1 ? 's' : ''}</span>}
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, color: text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 4 }}>
          {ticket.subject}
        </div>
        {/* Description preview */}
        {ticket.description && (
          <div style={{ fontSize: 12, color: muted, lineHeight: 1.5, marginBottom: tags.length ? 6 : 0, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {ticket.description.substring(0, 120)}{ticket.description.length > 120 ? '...' : ''}
          </div>
        )}
        {tags.length > 0 && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {tags.slice(0, 6).map((tag, i) => (
              <span key={i} style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10, background: 'rgba(124,58,237,0.12)', color: '#a78bfa', border: '1px solid rgba(124,58,237,0.25)' }}>{tag}</span>
            ))}
            {tags.length > 6 && <span style={{ fontSize: 10, color: '#64748b' }}>+{tags.length - 6}</span>}
          </div>
        )}
      </div>

      {/* Right side: result scores + expand button */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 }}>
        {/* Expand button */}
        <button
          onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
          title={expanded ? 'Collapse' : 'View full ticket'}
          style={{
            background: 'transparent',
            border: `1px solid ${expanded ? '#7c3aed' : border}`,
            color: expanded ? '#7c3aed' : '#64748b',
            borderRadius: 6, padding: '3px 8px', fontSize: 12,
            cursor: 'pointer', transition: 'all 0.15s',
          }}
        >
          {expanded ? '▲' : '▼'}
        </button>

        {/* Result scores if already benchmarked */}
        {hasResult && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 9, color: '#38bdf8', fontWeight: 700, marginBottom: 2 }}>HUMAN</div>
              <ScoreRing score={result.human?.score} color="#38bdf8" size={40} />
            </div>
            <div style={{ fontSize: 12, color: border }}>vs</div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 9, color: '#a78bfa', fontWeight: 700, marginBottom: 2 }}>AI</div>
              <ScoreRing score={result.ai?.score} color="#a78bfa" size={40} />
            </div>
          </div>
        )}
      </div>
      </div>{/* end main row */}

      {/* Expanded: full description + comment thread */}
      {expanded && (
        <div
          onClick={e => e.stopPropagation()}
          style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${border}` }}
        >
          {ticket.description && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', letterSpacing: '0.06em', marginBottom: 6 }}>DESCRIPTION</div>
              <div style={{ fontSize: 12, color: muted, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                {ticket.description}
              </div>
            </div>
          )}
          {ticket.comments?.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', letterSpacing: '0.06em', marginBottom: 8 }}>
                THREAD ({ticket.comments.length} comment{ticket.comments.length !== 1 ? 's' : ''})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {ticket.comments.map((comment, i) => {
                  const isAgent = comment.author_type === 'agent' || comment.role === 'agent';
                  return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: isAgent ? 'flex-end' : 'flex-start' }}>
                      <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2 }}>
                        {isAgent ? '🧑‍💼 Agent' : '👤 Customer'}
                        {comment.author && <span style={{ marginLeft: 4 }}>— {comment.author}</span>}
                      </div>
                      <div style={{
                        maxWidth: '85%', padding: '7px 11px', fontSize: 12,
                        color: text, lineHeight: 1.6, whiteSpace: 'pre-wrap',
                        borderRadius: isAgent ? '10px 4px 10px 10px' : '4px 10px 10px 10px',
                        background: isAgent ? 'rgba(56,189,248,0.07)' : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${isAgent ? 'rgba(56,189,248,0.2)' : border}`,
                      }}>
                        {comment.content || comment.body || comment.text || ''}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Result comparison card (expanded view) ────────────────────────────────────

const ResultCard = ({ ticket, border, text, muted, cardBg }) => {
  const [expanded, setExpanded] = useState(false);
  const [convView, setConvView] = useState('both');
  const noHuman = ticket.human?.outcome === 'no_response' || ticket.human?.status === 'skipped';
  const isKbGrounded = ticket.ai?.kb_grounded || false;
  const radarData = [
    { metric: 'Accuracy',   human: ticket.human?.accuracy || 0,   ai: ticket.ai?.accuracy || 0   },
    { metric: 'Tone',       human: ticket.human?.tone || 0,       ai: ticket.ai?.tone || 0       },
    { metric: 'Resolution', human: ticket.human?.resolution || 0, ai: ticket.ai?.resolution || 0 },
    { metric: 'Efficiency', human: ticket.human?.efficiency || 0, ai: ticket.ai?.efficiency || 0 },
    ...(isKbGrounded && ticket.ai?.groundedness != null ? [
      { metric: 'Groundedness', human: 0, ai: ticket.ai.groundedness }
    ] : []),
  ];
  const diff = ticket.comparison?.score_diff, winner = ticket.comparison?.winner;
  const bannerConfig = () => {
    if (winner === 'ai_only') return { text: '🤖 AI Only — No human agent response', bg: 'rgba(100,116,139,0.08)', bc: 'rgba(100,116,139,0.3)', color: '#94a3b8' };
    if (winner === 'tied')    return { text: '🤝 Tied', bg: 'rgba(245,158,11,0.1)', bc: 'rgba(245,158,11,0.3)', color: '#f59e0b' };
    if (winner === 'ai')      return { text: `🤖 AI wins by ${diff}pts`, bg: 'rgba(139,92,246,0.1)', bc: 'rgba(139,92,246,0.3)', color: '#a78bfa' };
    if (winner === 'human')   return { text: `👤 Human wins by ${Math.abs(diff)}pts`, bg: 'rgba(56,189,248,0.1)', bc: 'rgba(56,189,248,0.3)', color: '#38bdf8' };
    return { text: '—', bg: 'transparent', bc: 'rgba(100,116,139,0.3)', color: '#64748b' };
  };
  const banner = bannerConfig();
  return (
    <div style={{ background: cardBg, border: `1px solid ${border}`, borderRadius: 12, overflow: 'hidden' }}>
      <div onClick={() => setExpanded(!expanded)} style={{ padding: '14px 18px', cursor: 'pointer', display: 'flex', gap: 14, alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontSize: 11, color: '#64748b', fontFamily: 'monospace' }}>#{ticket.ticket_id}</span>
            {ticket.priority && <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: PRIORITY_COLOR[ticket.priority] || '#94a3b8' }}>{ticket.priority}</span>}
            {ticket.channel && <span style={{ fontSize: 11, color: '#64748b' }}>{ticket.channel}</span>}
            {ticket.status  && <span style={{ fontSize: 11, color: '#64748b' }}>{ticket.status}</span>}
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ticket.subject}</div>
        </div>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexShrink: 0 }}>
          <div style={{ textAlign: 'center' }}><div style={{ fontSize: 9, color: '#38bdf8', fontWeight: 700, marginBottom: 3 }}>HUMAN</div><ScoreRing score={ticket.human?.score} color="#38bdf8" /></div>
          <div style={{ fontSize: 14, color: border }}>vs</div>
          <div style={{ textAlign: 'center' }}><div style={{ fontSize: 9, color: '#a78bfa', fontWeight: 700, marginBottom: 3 }}>AI</div><ScoreRing score={ticket.ai?.score} color="#a78bfa" /></div>
          <div style={{ color: muted, fontSize: 14, marginLeft: 4 }}>{expanded ? '▲' : '▼'}</div>
        </div>
      </div>
      <div style={{ padding: '0 18px 12px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {!noHuman && <><div style={{ display: 'flex', gap: 5, alignItems: 'center' }}><span style={{ fontSize: 11, color: muted }}>Human:</span><OutcomeBadge outcome={ticket.human?.outcome} /></div><span style={{ color: border }}>·</span></>}
        <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}><span style={{ fontSize: 11, color: muted }}>AI:</span><OutcomeBadge outcome={ticket.ai?.outcome} /></div>
        <div style={{ marginLeft: 'auto', padding: '4px 12px', borderRadius: 8, background: banner.bg, border: `1px solid ${banner.bc}`, fontSize: 12, fontWeight: 600, color: banner.color }}>{banner.text}</div>
      </div>
      {expanded && (
        <div style={{ borderTop: `1px solid ${border}`, padding: 18 }}>
          <div style={{ display: 'grid', gridTemplateColumns: noHuman ? '1fr 200px' : '1fr 200px 1fr', gap: 20, marginBottom: 24 }}>
            {!noHuman && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#38bdf8', marginBottom: 10 }}>👤 HUMAN · {ticket.human?.turns || 0} turns</div>
                {['accuracy','tone','resolution','efficiency'].map(m => (
                  <div key={m} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: `1px solid ${border}` }}>
                    <span style={{ fontSize: 12, color: muted, textTransform: 'capitalize' }}>{m}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: (ticket.human?.[m]||0)>=4?'#10b981':(ticket.human?.[m]||0)>=3?'#f59e0b':'#ef4444' }}>{(ticket.human?.[m]||0).toFixed(1)}/5</span>
                  </div>
                ))}
                {ticket.human?.strengths?.length > 0 && <div style={{ marginTop: 10 }}><div style={{ fontSize: 10, color: '#10b981', fontWeight: 700, marginBottom: 5 }}>✓ STRENGTHS</div>{ticket.human.strengths.map((s,i)=><div key={i} style={{ fontSize: 12, color: muted, padding: '2px 0' }}>• {s}</div>)}</div>}
                {ticket.human?.improvements?.length > 0 && <div style={{ marginTop: 8 }}><div style={{ fontSize: 10, color: '#f59e0b', fontWeight: 700, marginBottom: 5 }}>→ IMPROVEMENTS</div>{ticket.human.improvements.map((s,i)=><div key={i} style={{ fontSize: 12, color: muted, padding: '2px 0' }}>• {s}</div>)}</div>}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ fontSize: 10, color: muted, fontWeight: 600, marginBottom: 5 }}>PERFORMANCE PROFILE</div>
              <ResponsiveContainer width="100%" height={210}>
                <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="68%">
                  <PolarGrid stroke="#1e293b" />
                  <PolarAngleAxis dataKey="metric" tick={{ fill: muted, fontSize: 10 }} />
                  <PolarRadiusAxis domain={[0, 5]} tick={false} axisLine={false} />
                  {!noHuman && <Radar name="Human" dataKey="human" stroke="#38bdf8" fill="#38bdf8" fillOpacity={0.15} strokeWidth={2} />}
                  <Radar name="AI" dataKey="ai" stroke="#a78bfa" fill="#a78bfa" fillOpacity={0.2} strokeWidth={2} />
                  <Tooltip formatter={(v, n) => [`${v}/5`, n === 'human' ? 'Human' : 'AI']} />
                </RadarChart>
              </ResponsiveContainer>
              {!noHuman && <div style={{ width: '100%', marginTop: 10 }}>
                <MetricRow label="Accuracy"   human={ticket.human?.accuracy||0}   ai={ticket.ai?.accuracy||0} />
                <MetricRow label="Tone"       human={ticket.human?.tone||0}       ai={ticket.ai?.tone||0} />
                <MetricRow label="Resolution" human={ticket.human?.resolution||0} ai={ticket.ai?.resolution||0} />
                <MetricRow label="Efficiency" human={ticket.human?.efficiency||0} ai={ticket.ai?.efficiency||0} />
              </div>}
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#a78bfa', marginBottom: 10 }}>
                🤖 AI · {ticket.ai?.turns || 0} turns
                {isKbGrounded && <span style={{ marginLeft: 8, fontSize: 10, padding: '2px 7px', borderRadius: 10, background: 'rgba(124,58,237,0.15)', color: '#a78bfa', border: '1px solid rgba(124,58,237,0.3)', fontWeight: 600 }}>📚 KB</span>}
              </div>
              {['accuracy','tone','resolution','efficiency'].map(m => (
                <div key={m} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: `1px solid ${border}` }}>
                  <span style={{ fontSize: 12, color: muted, textTransform: 'capitalize' }}>{m}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: (ticket.ai?.[m]||0)>=4?'#10b981':(ticket.ai?.[m]||0)>=3?'#f59e0b':'#ef4444' }}>{(ticket.ai?.[m]||0).toFixed(1)}/5</span>
                </div>
              ))}
              {isKbGrounded && ticket.ai?.groundedness != null && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: `1px solid ${border}` }}>
                  <span style={{ fontSize: 12, color: '#a78bfa', fontWeight: 600 }}>Groundedness</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: ticket.ai.groundedness >= 4 ? '#a78bfa' : ticket.ai.groundedness >= 3 ? '#f59e0b' : '#ef4444' }}>
                    {ticket.ai.groundedness.toFixed(1)}/5
                    <span style={{ marginLeft: 6, fontSize: 10, padding: '1px 6px', borderRadius: 8, background: ticket.ai.groundedness >= 4 ? 'rgba(167,139,250,0.15)' : ticket.ai.groundedness >= 3 ? 'rgba(245,158,11,0.15)' : 'rgba(239,68,68,0.15)' }}>
                      {ticket.ai.groundedness >= 4 ? 'Based on KB' : ticket.ai.groundedness >= 3 ? 'Partial' : 'Hallucinated'}
                    </span>
                  </span>
                </div>
              )}
              {ticket.ai?.hallucination_detected && (
                <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 6, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', fontSize: 11, color: '#ef4444' }}>
                  ⚠️ Hallucination detected
                  {ticket.ai.hallucination_examples?.length > 0 && (
                    <ul style={{ margin: '4px 0 0', paddingLeft: 14 }}>
                      {ticket.ai.hallucination_examples.map((ex, i) => <li key={i} style={{ marginTop: 2 }}>{ex}</li>)}
                    </ul>
                  )}
                </div>
              )}
              {ticket.ai?.strengths?.length > 0 && <div style={{ marginTop: 10 }}><div style={{ fontSize: 10, color: '#10b981', fontWeight: 700, marginBottom: 5 }}>✓ STRENGTHS</div>{ticket.ai.strengths.map((s,i)=><div key={i} style={{ fontSize: 12, color: muted, padding: '2px 0' }}>• {s}</div>)}</div>}
              {ticket.ai?.improvements?.length > 0 && <div style={{ marginTop: 8 }}><div style={{ fontSize: 10, color: '#f59e0b', fontWeight: 700, marginBottom: 5 }}>→ IMPROVEMENTS</div>{ticket.ai.improvements.map((s,i)=><div key={i} style={{ fontSize: 12, color: muted, padding: '2px 0' }}>• {s}</div>)}</div>}
            </div>
          </div>
          {/* Conversations */}
          <div style={{ borderTop: `1px solid ${border}`, paddingTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: muted, letterSpacing: '0.06em' }}>💬 CONVERSATIONS</div>
              {!noHuman && (
                <div style={{ display: 'flex', gap: 5 }}>
                  {['both','human','ai'].map(v => (
                    <button key={v} onClick={() => setConvView(v)} style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: '1px solid', borderColor: convView===v?'#7c3aed':border, background: convView===v?'rgba(124,58,237,0.15)':'transparent', color: convView===v?'#a78bfa':muted }}>
                      {v==='both'?'Side by Side':v==='human'?'👤 Human':'🤖 AI'}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: noHuman?'1fr':convView==='both'?'1fr 1fr':'1fr', gap: 16 }}>
              {!noHuman && (convView==='both'||convView==='human') && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#38bdf8', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid rgba(56,189,248,0.2)' }}>👤 HUMAN · {ticket.human?.turns||0} turns</div>
                  {(ticket.human?.conversation||[]).length > 0 ? ticket.human.conversation.map((m,i)=><ChatBubble key={i} msg={m} side="human" cardBg={cardBg} border={border} text={text} />) : <div style={{ padding: 16, textAlign: 'center', color: muted, fontSize: 13, background: cardBg, borderRadius: 8, border: `1px dashed ${border}` }}>No conversation transcript</div>}
                  <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}><OutcomeBadge outcome={ticket.human?.outcome} /></div>
                </div>
              )}
              {(convView==='both'||convView==='ai') && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#a78bfa', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid rgba(139,92,246,0.2)' }}>🤖 AI · {ticket.ai?.turns||0} turns</div>
                  {(ticket.ai?.conversation||[]).length > 0 ? ticket.ai.conversation.map((m,i)=><ChatBubble key={i} msg={m} side="ai" cardBg={cardBg} border={border} text={text} />) : <div style={{ padding: 16, textAlign: 'center', color: muted, fontSize: 13, background: cardBg, borderRadius: 8, border: `1px dashed ${border}` }}>No AI conversation</div>}
                  <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}><OutcomeBadge outcome={ticket.ai?.outcome} /></div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── ABResultsView — Side-by-side A/B comparison results ──────────────────────

const ABTicketRow = ({ tid, ra, rb, ticketWinner, border, text, muted, cardBg, isDark }) => {
  const [expanded, setExpanded] = React.useState(false);
  const aScore = ra?.ai?.score;
  const bScore = rb?.ai?.score;
  const subject = ra?.subject || rb?.subject || tid;
  const priority = ra?.priority || rb?.priority || '';
  const winColor = ticketWinner === 'a' ? '#38bdf8' : ticketWinner === 'b' ? '#a78bfa' : '#f59e0b';
  const winLabel = ticketWinner === 'a' ? 'A wins' : ticketWinner === 'b' ? 'B wins' : 'Tied';
  const metrics = ['accuracy', 'tone', 'resolution', 'efficiency'];

  return (
    <div style={{ borderRadius: 8, border: `1px solid ${expanded ? winColor + '60' : border}`, overflow: 'hidden', transition: 'border-color 0.15s' }}>
      {/* Summary row — clickable */}
      <div
        onClick={() => setExpanded(v => !v)}
        style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px 90px 28px', gap: 12, alignItems: 'center', padding: '10px 14px', cursor: 'pointer', background: isDark ? 'rgba(255,255,255,0.02)' : '#f8fafc' }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 10, color: muted, fontFamily: 'monospace', marginBottom: 2 }}>
            #{tid} {priority && <span style={{ color: PRIORITY_COLOR[priority] || muted, fontWeight: 700, textTransform: 'uppercase' }}>{priority}</span>}
          </div>
          <div style={{ fontSize: 13, color: text, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subject}</div>
        </div>
        <div style={{ textAlign: 'center', fontSize: 13, fontWeight: 600, color: ticketWinner === 'a' ? '#38bdf8' : muted }}>{aScore != null ? aScore : '—'}</div>
        <div style={{ textAlign: 'center', fontSize: 13, fontWeight: 600, color: ticketWinner === 'b' ? '#a78bfa' : muted }}>{bScore != null ? bScore : '—'}</div>
        <div style={{ textAlign: 'right' }}>
          <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 20, color: winColor, background: `${winColor}18`, border: `1px solid ${winColor}40` }}>{winLabel}</span>
        </div>
        <div style={{ color: expanded ? winColor : muted, fontSize: 12, textAlign: 'center' }}>{expanded ? '▲' : '▼'}</div>
      </div>

      {/* Expanded detail — per-metric comparison */}
      {expanded && (
        <div style={{ borderTop: `1px solid ${border}`, padding: '14px 16px', background: isDark ? 'rgba(0,0,0,0.2)' : '#fff' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            {/* Config A detail */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#38bdf8', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid rgba(56,189,248,0.2)' }}>
                🔵 Config A — {ra?.ai?.outcome || '—'}
              </div>
              {metrics.map(m => (
                <div key={m} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: `1px solid ${border}20` }}>
                  <span style={{ fontSize: 12, color: muted, textTransform: 'capitalize' }}>{m}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: (ra?.ai?.[m] || 0) >= 4 ? '#10b981' : (ra?.ai?.[m] || 0) >= 3 ? '#f59e0b' : '#ef4444' }}>
                    {(ra?.ai?.[m] || 0).toFixed(1)}/5
                  </span>
                </div>
              ))}
              {ra?.ai?.strengths?.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 10, color: '#10b981', fontWeight: 700, marginBottom: 4 }}>✓ STRENGTHS</div>
                  {ra.ai.strengths.slice(0, 2).map((s, i) => <div key={i} style={{ fontSize: 11, color: muted, padding: '1px 0' }}>• {s}</div>)}
                </div>
              )}
            </div>
            {/* Config B detail */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#a78bfa', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid rgba(139,92,246,0.2)' }}>
                🟣 Config B — {rb?.ai?.outcome || '—'}
              </div>
              {metrics.map(m => (
                <div key={m} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: `1px solid ${border}20` }}>
                  <span style={{ fontSize: 12, color: muted, textTransform: 'capitalize' }}>{m}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: (rb?.ai?.[m] || 0) >= 4 ? '#10b981' : (rb?.ai?.[m] || 0) >= 3 ? '#f59e0b' : '#ef4444' }}>
                    {(rb?.ai?.[m] || 0).toFixed(1)}/5
                  </span>
                </div>
              ))}
              {rb?.ai?.strengths?.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 10, color: '#10b981', fontWeight: 700, marginBottom: 4 }}>✓ STRENGTHS</div>
                  {rb.ai.strengths.slice(0, 2).map((s, i) => <div key={i} style={{ fontSize: 11, color: muted, padding: '1px 0' }}>• {s}</div>)}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ABResultsView = ({ data, theme, border, text, muted, cardBg, bg }) => {
  const isDark = theme !== 'light';
  const { config_a, config_b, comparison } = data;
  const { winner, metric_deltas, recommendation, ticket_winners, a_overall, b_overall } = comparison;

  const metrics = ['accuracy', 'tone', 'resolution', 'efficiency'];

  const winnerColor = winner === 'a' ? '#38bdf8' : winner === 'b' ? '#a78bfa' : '#f59e0b';
  const winnerBg    = winner === 'a' ? 'rgba(56,189,248,0.1)' : winner === 'b' ? 'rgba(139,92,246,0.1)' : 'rgba(245,158,11,0.1)';

  // Per-ticket results merged from both configs
  const resultsA = config_a.results || [];
  const resultsB = config_b.results || [];
  const ticketIds = [...new Set([...resultsA.map(r => String(r.ticket_id)), ...resultsB.map(r => String(r.ticket_id))])];
  const mapA = Object.fromEntries(resultsA.map(r => [String(r.ticket_id), r]));
  const mapB = Object.fromEntries(resultsB.map(r => [String(r.ticket_id), r]));

  // Radar data — aggregate metrics A vs B
  const radarData = metrics.map(m => ({
    metric: m.charAt(0).toUpperCase() + m.slice(1),
    A: metric_deltas[m]?.a || 0,
    B: metric_deltas[m]?.b || 0,
  }));

  return (
    <div>
      {/* ── Winner callout + radar ── */}
      <div style={{ background: winnerBg, border: `2px solid ${winnerColor}`, borderRadius: 16, padding: '20px 28px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
        {/* Left: winner text */}
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: winnerColor, letterSpacing: '0.08em', marginBottom: 6 }}>OVERALL WINNER</div>
          <div style={{ fontSize: 26, fontWeight: 900, color: winnerColor }}>
            {winner === 'tied' ? '🤝 Tied' : winner === 'a' ? `🏆 ${config_a.name}` : `🏆 ${config_b.name}`}
          </div>
          <div style={{ fontSize: 13, color: text, marginTop: 8, lineHeight: 1.6 }}>{recommendation}</div>
        </div>

        {/* Centre: radar chart */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
          <div style={{ fontSize: 10, color: muted, fontWeight: 600, marginBottom: 6, letterSpacing: '0.06em' }}>PERFORMANCE PROFILE</div>
          <ResponsiveContainer width={220} height={180}>
            <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="55%">
              <PolarGrid stroke={isDark ? '#1e293b' : '#e2e8f0'} />
              <PolarAngleAxis dataKey="metric" tick={{ fill: muted, fontSize: 10 }} />
              <PolarRadiusAxis domain={[0, 5]} tick={false} axisLine={false} />
              <Radar name="Config A" dataKey="A" stroke="#38bdf8" fill="#38bdf8" fillOpacity={0.15} strokeWidth={2} />
              <Radar name="Config B" dataKey="B" stroke="#a78bfa" fill="#a78bfa" fillOpacity={0.15} strokeWidth={2} />
              <Tooltip formatter={(v, n) => [`${v}/5`, n]} />
            </RadarChart>
          </ResponsiveContainer>
          <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
            <span style={{ fontSize: 10, color: '#38bdf8', fontWeight: 700 }}>— A: {config_a.name}</span>
            <span style={{ fontSize: 10, color: '#a78bfa', fontWeight: 700 }}>— B: {config_b.name}</span>
          </div>
        </div>

        {/* Right: score rings */}
        <div style={{ display: 'flex', gap: 20, flexShrink: 0, alignItems: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#38bdf8', letterSpacing: '0.06em', marginBottom: 6 }}>CONFIG A</div>
            <div style={{ fontSize: 11, color: muted, marginBottom: 6, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{config_a.name}</div>
            <ScoreRing score={Math.round(a_overall)} color="#38bdf8" size={60} />
          </div>
          <div style={{ fontSize: 14, color: muted, fontWeight: 700 }}>vs</div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#a78bfa', letterSpacing: '0.06em', marginBottom: 6 }}>CONFIG B</div>
            <div style={{ fontSize: 11, color: muted, marginBottom: 6, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{config_b.name}</div>
            <ScoreRing score={Math.round(b_overall)} color="#a78bfa" size={60} />
          </div>
        </div>
      </div>

      {/* ── Metric delta table ── */}
      <div style={{ background: cardBg, border: `1px solid ${border}`, borderRadius: 12, padding: '16px 20px', marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: muted, letterSpacing: '0.06em', marginBottom: 14 }}>METRIC BREAKDOWN</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px', gap: 0 }}>
          <div style={{ fontSize: 10, color: muted, fontWeight: 700, paddingBottom: 8, borderBottom: `1px solid ${border}` }}>METRIC</div>
          <div style={{ fontSize: 10, color: '#38bdf8', fontWeight: 700, textAlign: 'center', paddingBottom: 8, borderBottom: `1px solid ${border}` }}>CONFIG A</div>
          <div style={{ fontSize: 10, color: '#a78bfa', fontWeight: 700, textAlign: 'center', paddingBottom: 8, borderBottom: `1px solid ${border}` }}>CONFIG B</div>
          <div style={{ fontSize: 10, color: muted, fontWeight: 700, textAlign: 'center', paddingBottom: 8, borderBottom: `1px solid ${border}` }}>DELTA</div>
          {metrics.map(m => {
            const d = metric_deltas[m] || { a: 0, b: 0, delta: 0 };
            const aWins = d.a > d.b + 0.1;
            const bWins = d.b > d.a + 0.1;
            return (
              <React.Fragment key={m}>
                <div style={{ padding: '8px 0', borderBottom: `1px solid ${border}20`, fontSize: 13, color: text, textTransform: 'capitalize' }}>{m}</div>
                <div style={{ padding: '8px 0', borderBottom: `1px solid ${border}20`, textAlign: 'center', fontSize: 13, fontWeight: aWins ? 700 : 400, color: aWins ? '#38bdf8' : muted }}>{d.a.toFixed(1)}/5{aWins ? ' ◀' : ''}</div>
                <div style={{ padding: '8px 0', borderBottom: `1px solid ${border}20`, textAlign: 'center', fontSize: 13, fontWeight: bWins ? 700 : 400, color: bWins ? '#a78bfa' : muted }}>{bWins ? '▶ ' : ''}{d.b.toFixed(1)}/5</div>
                <div style={{ padding: '8px 0', borderBottom: `1px solid ${border}20`, textAlign: 'center', fontSize: 12, fontWeight: 700, color: d.delta > 0.1 ? '#a78bfa' : d.delta < -0.1 ? '#38bdf8' : muted }}>
                  {d.delta > 0 ? '+' : ''}{d.delta.toFixed(2)}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* ── Per-ticket expandable rows ── */}
      <div style={{ background: cardBg, border: `1px solid ${border}`, borderRadius: 12, padding: '16px 20px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: muted, letterSpacing: '0.06em', marginBottom: 12 }}>
          PER-TICKET COMPARISON ({ticketIds.length} tickets) <span style={{ fontWeight: 400, fontSize: 10 }}>— click to expand</span>
        </div>
        {ticketIds.length === 0 && <div style={{ color: muted, fontSize: 13, padding: '20px 0', textAlign: 'center' }}>No ticket results available.</div>}
        {ticketIds.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px 90px 28px', gap: 12, padding: '0 14px', marginBottom: 6 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: muted }}>TICKET</div>
            <div style={{ textAlign: 'center', fontSize: 10, fontWeight: 700, color: '#38bdf8' }}>A SCORE</div>
            <div style={{ textAlign: 'center', fontSize: 10, fontWeight: 700, color: '#a78bfa' }}>B SCORE</div>
            <div style={{ textAlign: 'right', fontSize: 10, fontWeight: 700, color: muted }}>WINNER</div>
            <div />
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {ticketIds.map(tid => (
            <ABTicketRow
              key={tid}
              tid={tid}
              ra={mapA[tid]}
              rb={mapB[tid]}
              ticketWinner={ticket_winners[tid] || 'tied'}
              border={border}
              text={text}
              muted={muted}
              cardBg={cardBg}
              isDark={isDark}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Main BenchmarkTab ─────────────────────────────────────────────────────────

export default function BenchmarkTab({ theme, sharedKbArticles = null, sharedAgentPrompt = '', onUpgradeNeeded, isDemo = false, betaCta = '' }) {
  const isDark = theme !== 'light';
  const bg = isDark ? '#0a0f1e' : '#f8fafc';
  const cardBg = isDark ? '#0f172a' : '#ffffff';
  const border = isDark ? '#1e293b' : '#e2e8f0';
  const text = isDark ? '#e2e8f0' : '#1e293b';
  const muted = isDark ? '#64748b' : '#94a3b8';

  const { billing } = useBilling();
  const canExportCsv = billing?.features?.csv_export !== false;
  const canAb = billing?.features?.ab_comparison === true;

  // ── Imported tickets ──────────────────────────────────────────────
  const { authFetch } = useApi();
  const [importedTickets, setImportedTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // ── Filters ──────────────────────────────────────────────────────
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterStatus,   setFilterStatus]   = useState('all');
  const [filterChannel,  setFilterChannel]  = useState('all');
  const [filterWinner,   setFilterWinner]   = useState('all');
  const [minComments,    setMinComments]    = useState(0);         // 0 = no minimum
  // ROI calculator inputs — persisted to localStorage
  const [humanCostInput,   setHumanCostInput]   = useState(() => parseFloat(localStorage.getItem('roi_humanCost')   || '8'));
  const [monthlyVolInput,  setMonthlyVolInput]  = useState(() => parseInt(localStorage.getItem('roi_monthlyVol')    || '0'));  // 0 = auto (evaluated * 20)
  const [zdPriceInput,     setZdPriceInput]     = useState(() => parseFloat(localStorage.getItem('roi_zdPrice')       || '2'));  // Zendesk outcome-based price per AI resolution
  const [showRoiCalc,      setShowRoiCalc]      = useState(false);  // toggle explanation
  const [selectedTags,   setSelectedTags]   = useState(new Set());   // multi-select
  const [searchId,       setSearchId]       = useState('');

  // ── Benchmark run state ───────────────────────────────────────────
  const [benchmarkData, setBenchmarkData] = useState(null);
  const [loading,        setLoading]       = useState(false);
  const [loadingExisting,setLoadingExisting] = useState(true);
  const [error,          setError]         = useState(null);
  const [progress,       setProgress]      = useState(0);
  const [progressMsg,    setProgressMsg]   = useState('');
  const [runningTicketCount, setRunningTicketCount] = useState(0);

  // ── View mode ─────────────────────────────────────────────────────
  // 'select' = browsing/selecting tickets | 'results' = viewing benchmark results
  const [viewMode, setViewMode] = useState('select');

  // ── A/B comparison state (Pro tier) ──────────────────────────────
  const [abMode,    setAbMode]    = useState(false);
  const [abConfigA, setAbConfigA] = useState('');
  const [abConfigB, setAbConfigB] = useState('');
  const [abData,    setAbData]    = useState(null);
  const [agentConfigs, setAgentConfigs] = useState([]);

  // ── Ticket load error (visible, not silently swallowed) ──────────
  const [ticketLoadError, setTicketLoadError] = useState(null);

  // ── Load imported tickets ─────────────────────────────────────────
  useEffect(() => {
    loadTickets();
    loadExistingResults();
    loadAgentConfigs();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTickets = async () => {
    setLoadingTickets(true);
    setTicketLoadError(null);
    try {
      const res = await authFetch(`/api/zendesk/tickets?limit=1000`);
      setImportedTickets(res.tickets || []);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Could not load tickets from backend';
      setTicketLoadError(msg);
      setImportedTickets([]);
    } finally {
      setLoadingTickets(false);
    }
  };

  const loadAgentConfigs = async () => {
    try {
      const res = await authFetch('/api/agent-configs');
      setAgentConfigs(res.configs || res || []);
    } catch {}
  };

  const loadExistingResults = async () => {
    try {
      const res = await authFetch(`/api/benchmark/summary`);
      if (res.status === 'success') {
        const r2 = await authFetch(`/api/benchmark/results?limit=500`);
        setBenchmarkData({ summary: res.summary, results: r2.results || [] });
      }
    } catch {}
    finally { setLoadingExisting(false); }
  };

  // ── Derived: all unique tags from imported tickets ────────────────
  const allTags = useMemo(() => {
    const tagSet = new Set();
    importedTickets.forEach(t => (t.tags || []).forEach(tag => tagSet.add(tag)));
    return [...tagSet].sort();
  }, [importedTickets]);

  // ── Results map: ticket_id → result ──────────────────────────────
  const resultByTicketId = useMemo(() => {
    const map = {};
    (benchmarkData?.results || []).forEach(r => {
      if (r.ticket_id) map[String(r.ticket_id)] = r;
    });
    return map;
  }, [benchmarkData]);

  // ── Filtered visible tickets ──────────────────────────────────────
  const visibleTickets = useMemo(() => {
    const filtered = importedTickets.filter(t => {
      const tid = String(t.zendesk_ticket_id || t.id || '');
      if (filterPriority !== 'all' && t.priority !== filterPriority) return false;
      if (filterStatus   !== 'all' && t.status   !== filterStatus)   return false;
      if (filterChannel  !== 'all' && t.channel  !== filterChannel)  return false;
      if (searchId.trim() && !tid.includes(searchId.trim())) return false;
      if (selectedTags.size > 0) {
        const ticketTags = new Set(t.tags || []);
        for (const tag of selectedTags) {
          if (!ticketTags.has(tag)) return false;
        }
      }
      // Minimum comments filter
      if (minComments > 0) {
        const commentCount = (t.comments || []).length;
        if (commentCount < minComments) return false;
      }
      // Winner filter only applies if the ticket has a result
      if (filterWinner !== 'all') {
        const result = resultByTicketId[tid];
        if (!result) return false;
        const w = result.comparison?.winner;
        if (filterWinner === 'ai'    && w !== 'ai')    return false;
        if (filterWinner === 'human' && w !== 'human') return false;
        if (filterWinner === 'tied'  && !['tied','ai_only'].includes(w)) return false;
      }
      return true;
    });
    // Sort: selected tickets first, unselected below
    return filtered.sort((a, b) => {
      const aSel = selectedIds.has(String(a.zendesk_ticket_id || a.id || ''));
      const bSel = selectedIds.has(String(b.zendesk_ticket_id || b.id || ''));
      if (aSel && !bSel) return -1;
      if (!aSel && bSel) return 1;
      return 0;
    });
  }, [importedTickets, filterPriority, filterStatus, filterChannel, searchId, selectedTags, filterWinner, resultByTicketId, selectedIds, minComments]);

  // ── Unique filter values ──────────────────────────────────────────
  const uniqueStatuses  = [...new Set(importedTickets.map(t => t.status).filter(Boolean))];
  const uniqueChannels  = [...new Set(importedTickets.map(t => t.channel).filter(Boolean))];

  // ── Selection helpers ─────────────────────────────────────────────
  const toggleTicket = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      const key = String(id);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(visibleTickets.map(t => String(t.zendesk_ticket_id || t.id))));
  const deselectAll = () => setSelectedIds(new Set());

  const toggleTag = (tag) => {
    setSelectedTags(prev => {
      const next = new Set(prev);
      next.has(tag) ? next.delete(tag) : next.add(tag);
      return next;
    });
  };

  const clearFilters = () => {
    setFilterPriority('all'); setFilterStatus('all'); setFilterChannel('all');
    setFilterWinner('all'); setSelectedTags(new Set()); setSearchId(''); setMinComments(0);
  };
  const hasActiveFilters = filterPriority !== 'all' || filterStatus !== 'all' || filterChannel !== 'all' || filterWinner !== 'all' || selectedTags.size > 0 || searchId || minComments > 0;

  // ── RunTestModal state ────────────────────────────────────────────
  const [benchmarkModalOpen, setBenchmarkModalOpen] = useState(false);

  // ── Run benchmark ─────────────────────────────────────────────────
  const runWithProgress = async (apiFn, msgs, targetView = 'results') => {
    setLoading(true); setError(null); setProgress(0); setProgressMsg(msgs[0]);
    const start = Date.now();
    const iv = setInterval(() => {
      const p = Math.min(88, Math.round(40 * Math.log10((Date.now()-start)/1000 + 1)));
      setProgress(p);
      setProgressMsg(msgs[Math.min(p < 30 ? 0 : p < 60 ? 1 : p < 80 ? 2 : 3, msgs.length-1)]);
    }, 1500);
    try {
      const data = await apiFn();
      clearInterval(iv);
      setProgress(100); setProgressMsg('✓ Benchmark complete!');
      setTimeout(() => { if (targetView === 'results') setBenchmarkData(data); setProgress(0); setProgressMsg(''); setViewMode(targetView); }, 800);
    } catch (e) {
      clearInterval(iv);
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg||JSON.stringify(d)).join('; ') : 'Benchmark failed');
      setProgress(0); setProgressMsg('');
    } finally {
      setTimeout(() => setLoading(false), 900);
    }
  };

  // Opens the KB/prompt config modal before running — or uses shared config directly
  const runOnSelected = () => {
    const ticketIds = [...selectedIds];
    if (!ticketIds.length) return;
    // If shared KB config is set from the nav, run immediately without modal
    if (sharedKbArticles !== null || sharedAgentPrompt) {
      executeOnSelected({
        kb_articles: sharedKbArticles,
        agent_system_prompt: sharedAgentPrompt || null,
      });
      return;
    }
    setBenchmarkModalOpen(true);
  };

  // Called when the modal is confirmed — receives { kb_articles, agent_system_prompt }
  const executeOnSelected = ({ kb_articles, agent_system_prompt }) => {
    const ticketIds = [...selectedIds];
    setRunningTicketCount(ticketIds.length);
    runWithProgress(
      () => authFetch(`/api/benchmark/batch`, { method: 'POST', body: JSON.stringify({
        ticket_ids: ticketIds,
        kb_articles: kb_articles || null,
        agent_system_prompt: agent_system_prompt || null,
      }) }),
      ['Loading selected tickets...', 'Evaluating human agent conversations...', 'Running AI simulations...', 'Scoring and comparing results...']
    );
  };

  const exportAbResults = () => {
    if (!abData) return;
    const { config_a, config_b, comparison } = abData;
    const { metric_deltas, winner, a_overall, b_overall, ticket_winners } = comparison;
    const metrics = ['accuracy', 'tone', 'resolution', 'efficiency'];

    // Summary sheet rows
    const summaryRows = [
      ['A/B Comparison Export'],
      ['Config A', config_a.name],
      ['Config B', config_b.name],
      ['Overall Winner', winner === 'a' ? config_a.name : winner === 'b' ? config_b.name : 'Tied'],
      ['Config A Score', a_overall],
      ['Config B Score', b_overall],
      [],
      ['METRIC', 'CONFIG A', 'CONFIG B', 'DELTA (B-A)'],
      ...metrics.map(m => {
        const d = metric_deltas[m] || { a: 0, b: 0, delta: 0 };
        return [m.charAt(0).toUpperCase() + m.slice(1), d.a.toFixed(2), d.b.toFixed(2), (d.delta > 0 ? '+' : '') + d.delta.toFixed(2)];
      }),
      [],
      ['TICKET ID', 'SUBJECT', 'PRIORITY', 'A SCORE', 'B SCORE', 'WINNER',
       ...metrics.flatMap(m => [`A ${m}`, `B ${m}`])],
    ];

    // Per-ticket rows
    const mapA = Object.fromEntries((config_a.results || []).map(r => [String(r.ticket_id), r]));
    const mapB = Object.fromEntries((config_b.results || []).map(r => [String(r.ticket_id), r]));
    const ticketIds = [...new Set([
      ...(config_a.results || []).map(r => String(r.ticket_id)),
      ...(config_b.results || []).map(r => String(r.ticket_id)),
    ])];

    const ticketRows = ticketIds.map(tid => {
      const ra = mapA[tid]; const rb = mapB[tid];
      const tw = ticket_winners[tid] || 'tied';
      return [
        tid,
        `"${(ra?.subject || rb?.subject || '').replace(/"/g, '""')}"`,
        ra?.priority || rb?.priority || '',
        ra?.ai?.score ?? '',
        rb?.ai?.score ?? '',
        tw === 'a' ? config_a.name : tw === 'b' ? config_b.name : 'Tied',
        ...metrics.flatMap(m => [
          ra?.ai?.[m] != null ? (ra.ai[m] / 5).toFixed(2) : '',
          rb?.ai?.[m] != null ? (rb.ai[m] / 5).toFixed(2) : '',
        ]),
      ];
    });

    const allRows = [...summaryRows, ...ticketRows];
    const csv = allRows.map(r => r.join(',')).join('\n');
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })),
      download: `ab-comparison-${Date.now()}.csv`,
    });
    a.click();
  };

  const runMockBenchmark = () => {
    setRunningTicketCount(10);
    runWithProgress(
      () => authFetch(`/api/benchmark/mock-run`, { method: 'POST', body: JSON.stringify({ count: 10 }) }),
      ['Evaluating human agent conversations...', 'Running AI simulations...', 'Scoring with LLM judge...', 'Generating comparison report...']
    );
  };

  const clearResults = async () => {
    if (!window.confirm('Clear all benchmark results? This cannot be undone.')) return;
    try { await authFetch(`/api/benchmark/results`, { method: 'DELETE' }); } catch {}
    setBenchmarkData(null);
  };

  const runAbBenchmark = () => {
    const ticketIds = [...selectedIds];
    if (!ticketIds.length) return;
    if (!abConfigA || !abConfigB) return;
    if (abConfigA === abConfigB) {
      setError('Config A and Config B must be different.');
      return;
    }
    setError(null);
    setRunningTicketCount(ticketIds.length);
    runWithProgress(
      async () => {
        const data = await authFetch('/api/benchmark/ab-run', {
          method: 'POST',
          body: JSON.stringify({ ticket_ids: ticketIds, config_a_id: abConfigA, config_b_id: abConfigB }),
        });
        setAbData(data);
        return data;
      },
      ['Fetching agent configs...', 'Running Config A simulations...', 'Running Config B simulations...', 'Comparing results...'],
      'ab_results'
    );
  };

  const exportResults = () => {
    const results = benchmarkData?.results || [];
    const isKbRun = benchmarkData?.kb_grounded || results.some(r => r.ai?.kb_grounded);
    const headers = ['Ticket ID','Subject','Priority','Status','Channel','Winner','Human Score','AI Score','Score Diff','H Accuracy','H Tone','H Resolution','H Efficiency','AI Accuracy','AI Tone','AI Resolution','AI Efficiency', ...(isKbRun ? ['AI Groundedness','Hallucination'] : [])];
    const rows = results.map(r => [
      r.ticket_id,
      `"${(r.subject||'').replace(/"/g,'""')}"`,
      r.priority||'', r.status||'', r.channel||'',
      r.comparison?.winner||'',
      r.human?.score||'', r.ai?.score||'', r.comparison?.score_diff||'',
      r.human?.accuracy != null ? (r.human.accuracy/5).toFixed(2) : '',
      r.human?.tone      != null ? (r.human.tone/5).toFixed(2)     : '',
      r.human?.resolution!= null ? (r.human.resolution/5).toFixed(2): '',
      r.human?.efficiency!= null ? (r.human.efficiency/5).toFixed(2): '',
      r.ai?.accuracy  != null ? (r.ai.accuracy/5).toFixed(2)  : '',
      r.ai?.tone      != null ? (r.ai.tone/5).toFixed(2)      : '',
      r.ai?.resolution!= null ? (r.ai.resolution/5).toFixed(2): '',
      r.ai?.efficiency!= null ? (r.ai.efficiency/5).toFixed(2): '',
      ...(isKbRun ? [r.ai?.groundedness != null ? (r.ai.groundedness/5).toFixed(2) : '', r.ai?.hallucination_detected ? 'Yes' : 'No'] : []),
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(new Blob([csv],{type:'text/csv'})), download: `benchmark-${Date.now()}.csv` });
    a.click();
  };

  // ── Filtered results for results view ────────────────────────────
  const filteredResults = useMemo(() => {
    const filtered = (benchmarkData?.results || []).filter(t => {
      if (filterWinner !== 'all') {
        const w = t.comparison?.winner;
        if (filterWinner === 'ai'    && w !== 'ai')    return false;
        if (filterWinner === 'human' && w !== 'human') return false;
        if (filterWinner === 'tied'  && !['tied','ai_only'].includes(w)) return false;
      }
      if (filterPriority !== 'all' && t.priority !== filterPriority) return false;
      if (filterStatus   !== 'all' && t.status   !== filterStatus)   return false;
      if (filterChannel  !== 'all' && t.channel  !== filterChannel)  return false;
      if (searchId.trim() && !String(t.ticket_id).includes(searchId.trim())) return false;
      return true;
    });
    // Sort: full human+AI comparisons first, AI-only (no human data) last
    return filtered.sort((a, b) => {
      const aIsAiOnly = a.comparison?.winner === 'ai_only';
      const bIsAiOnly = b.comparison?.winner === 'ai_only';
      if (aIsAiOnly && !bIsAiOnly) return 1;
      if (!aIsAiOnly && bIsAiOnly) return -1;
      return 0;
    });
  }, [benchmarkData, filterWinner, filterPriority, filterStatus, filterChannel, searchId]);

  const summary = benchmarkData?.summary;
  const selectedCount = selectedIds.size;
  const benchmarkedIds = new Set(Object.keys(resultByTicketId));

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div style={{ background: bg, minHeight: '100vh', color: text, fontFamily: 'system-ui, sans-serif' }}>

      {/* ── Shared KB config status banner ──────────────────────── */}
      {sharedKbArticles && (
        <div style={{
          margin: '0', padding: '8px 28px',
          background: 'rgba(124,58,237,0.08)',
          borderBottom: '1px solid rgba(124,58,237,0.2)',
          display: 'flex', alignItems: 'center', gap: 10,
          fontSize: 13, color: '#a78bfa',
        }}>
          <span>📚</span>
          <span>
            <strong>{sharedKbArticles.length} KB article{sharedKbArticles.length !== 1 ? 's' : ''}</strong> active
            {sharedAgentPrompt && <> · Custom agent prompt set</>}
            {' '}— benchmark will include Groundedness scoring.
          </span>
          <span style={{ marginLeft: 'auto', fontSize: 11, opacity: 0.7 }}>
            Change via 📚 KB in the nav
          </span>
        </div>
      )}

      {/* ── Header ──────────────────────────────────────────────── */}
      <div style={{ background: isDark ? 'linear-gradient(180deg,#0f172a 0%,#0a0f1e 100%)' : '#fff', borderBottom: `1px solid ${border}`, padding: '20px 28px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: text }}>⚖️ Human vs AI Benchmark</h2>
            <p style={{ margin: '3px 0 0', fontSize: 12, color: muted }}>Select tickets → run benchmark → compare human vs AI performance</p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* View toggle */}
            {(benchmarkData || abData) && (
              <div style={{ display: 'flex', border: `1px solid ${border}`, borderRadius: 8, overflow: 'hidden' }}>
                {[['select', '🎯 Select'], ['results', '📊 Results'], ...(abData ? [['ab_results', '⚖️ A/B']] : [])].map(([mode, label]) => (
                  <button key={mode} onClick={() => setViewMode(mode)} style={{ padding: '7px 14px', border: 'none', background: viewMode === mode ? '#7c3aed' : 'transparent', color: viewMode === mode ? 'white' : muted, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{label}</button>
                ))}
              </div>
            )}
            {abData && (
              <button
                onClick={exportAbResults}
                title="Export A/B comparison to CSV"
                style={{ padding: '7px 14px', borderRadius: 8, fontSize: 12, cursor: 'pointer', border: `1px solid ${border}`, background: 'transparent', color: muted, display: 'flex', alignItems: 'center', gap: 5 }}
              >
                📥 Export A/B
              </button>
            )}
            {benchmarkData && <>
              <button
                onClick={canExportCsv ? exportResults : () => isDemo ? window.location.href = betaCta : onUpgradeNeeded && onUpgradeNeeded()}
                title={canExportCsv ? 'Export results to CSV' : 'CSV export requires Starter plan or above'}
                style={{
                  padding: '7px 14px', borderRadius: 8, fontSize: 12, cursor: 'pointer',
                  border: `1px solid ${canExportCsv ? border : 'rgba(124,58,237,0.3)'}`,
                  background: canExportCsv ? 'transparent' : 'rgba(124,58,237,0.08)',
                  color: canExportCsv ? muted : '#a78bfa',
                  display: 'flex', alignItems: 'center', gap: 5,
                }}
              >
                {canExportCsv ? '📥' : '🔒'} Export CSV
              </button>
              <button onClick={clearResults}  style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid rgba(239,68,68,0.3)', background: 'transparent', color: '#ef4444', fontSize: 12, cursor: 'pointer' }}>🗑️ Clear</button>
            </>}
            <button onClick={runMockBenchmark} disabled={loading} style={{ padding: '7px 14px', borderRadius: 8, border: `1px solid ${border}`, background: 'transparent', color: muted, fontSize: 12, cursor: loading ? 'not-allowed' : 'pointer' }}>🎭 Demo</button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '20px 28px' }}>

        {/* ── Progress ───────────────────────────────────────────── */}
        {loading && (
          <div style={{ background: cardBg, border: `1px solid ${border}`, borderRadius: 12, padding: 18, marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 13, color: text }}>{progressMsg}</span>
              <span style={{ fontSize: 13, color: '#a78bfa', fontWeight: 700 }}>{progress}%</span>
            </div>
            <div style={{ height: 8, background: border, borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${progress}%`, background: 'linear-gradient(90deg,#7c3aed,#a78bfa)', borderRadius: 4, transition: 'width 0.4s ease' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
              <span style={{ fontSize: 12, color: muted }}>⏱️ Evaluating human conversations + running AI simulations — ~30-60 seconds</span>
              {runningTicketCount > 0 && progress > 0 && progress < 100 && (
                <span style={{ fontSize: 12, color: '#a78bfa', fontWeight: 600, fontFamily: 'monospace', flexShrink: 0, marginLeft: 12 }}>
                  Ticket {Math.min(Math.max(1, Math.ceil(progress / 100 * runningTicketCount)), runningTicketCount)} of {runningTicketCount}
                </span>
              )}
            </div>
          </div>
        )}

        {/* ── Error ─────────────────────────────────────────────── */}
        {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, color: '#ef4444', fontSize: 13 }}>⚠️ {error}</div>}

        {/* ══════════════════════════════════════════════════════════
            SELECT VIEW — Browse, filter, select tickets
            ══════════════════════════════════════════════════════════ */}
        {viewMode === 'select' && (
          <>
            {/* Run bar */}
            <div style={{ background: cardBg, border: `1px solid ${selectedCount > 0 ? '#7c3aed' : border}`, borderRadius: 12, padding: '14px 18px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, color: text, fontWeight: 600 }}>
                  {selectedCount > 0 ? `${selectedCount} ticket${selectedCount !== 1 ? 's' : ''} selected` : 'Select tickets to benchmark'}
                </span>
                {selectedCount > 0 && <span style={{ fontSize: 12, color: muted }}>({[...selectedIds].filter(id => benchmarkedIds.has(id)).length} already benchmarked)</span>}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button onClick={selectAll} style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${border}`, background: 'transparent', color: muted, fontSize: 12, cursor: 'pointer' }}>Select All ({visibleTickets.length})</button>
                {selectedCount > 0 && <button onClick={deselectAll} style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${border}`, background: 'transparent', color: muted, fontSize: 12, cursor: 'pointer' }}>Deselect All</button>}

                {/* A/B mode toggle — Pro tier */}
                <div style={{ display: 'flex', border: `1px solid ${border}`, borderRadius: 8, overflow: 'hidden' }}>
                  <button
                    onClick={() => setAbMode(false)}
                    style={{ padding: '5px 12px', fontSize: 11, fontWeight: 600, border: 'none', background: !abMode ? '#7c3aed' : 'transparent', color: !abMode ? 'white' : muted, cursor: 'pointer' }}
                  >Single</button>
                  <button
                    onClick={() => {
                      if (!canAb) { isDemo ? window.location.href = betaCta : onUpgradeNeeded && onUpgradeNeeded(); return; }
                      setAbMode(true);
                    }}
                    title={canAb ? 'Run two configs side-by-side' : 'A/B comparison requires Professional plan'}
                    style={{ padding: '5px 12px', fontSize: 11, fontWeight: 600, border: 'none', background: abMode ? '#7c3aed' : 'transparent', color: abMode ? 'white' : canAb ? muted : '#a78bfa', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                  >{canAb ? '' : '🔒 '}A/B</button>
                </div>

                {/* A/B config pickers */}
                {abMode && canAb && (
                  <>
                    <select
                      value={abConfigA} onChange={e => setAbConfigA(e.target.value)}
                      style={{ padding: '5px 8px', borderRadius: 8, border: `1px solid ${abConfigA ? '#38bdf8' : border}`, background: bg, color: text, fontSize: 12, maxWidth: 160 }}
                    >
                      <option value="">Config A…</option>
                      {agentConfigs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <span style={{ fontSize: 12, color: muted, fontWeight: 700 }}>vs</span>
                    <select
                      value={abConfigB} onChange={e => setAbConfigB(e.target.value)}
                      style={{ padding: '5px 8px', borderRadius: 8, border: `1px solid ${abConfigB ? '#a78bfa' : border}`, background: bg, color: text, fontSize: 12, maxWidth: 160 }}
                    >
                      <option value="">Config B…</option>
                      {agentConfigs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </>
                )}

                <button
                  onClick={abMode && canAb ? runAbBenchmark : runOnSelected}
                  disabled={loading || selectedCount === 0 || (abMode && canAb && (!abConfigA || !abConfigB))}
                  style={{ padding: '7px 18px', borderRadius: 8, border: 'none', background: selectedCount > 0 && !loading ? 'linear-gradient(135deg,#7c3aed,#4f46e5)' : border, color: 'white', fontSize: 13, fontWeight: 600, cursor: selectedCount > 0 && !loading ? 'pointer' : 'not-allowed' }}
                >
                  {loading ? '⏳ Running...' : abMode && canAb ? `⚖️ Run A/B (${selectedCount})` : `▶ Run Benchmark (${selectedCount})`}
                </button>
              </div>
            </div>

            {/* Filter bar */}
            <div style={{ background: cardBg, border: `1px solid ${border}`, borderRadius: 12, padding: '14px 16px', marginBottom: 14 }}>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: allTags.length > 0 ? 12 : 0 }}>

                {/* ── Select All checkbox ── */}
                {(() => {
                  const allVisible = visibleTickets.length > 0;
                  const allSelected = allVisible && visibleTickets.every(t => selectedIds.has(String(t.zendesk_ticket_id || t.id)));
                  const someSelected = allVisible && visibleTickets.some(t => selectedIds.has(String(t.zendesk_ticket_id || t.id))) && !allSelected;
                  return (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', paddingRight: 10, borderRight: `1px solid ${border}`, userSelect: 'none' }}
                      title={allSelected ? 'Deselect all visible tickets' : 'Select all visible tickets'}>
                      <div
                        onClick={() => allSelected ? deselectAll() : selectAll()}
                        style={{
                          width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                          border: `2px solid ${allSelected || someSelected ? '#7c3aed' : border}`,
                          background: allSelected ? '#7c3aed' : someSelected ? 'rgba(124,58,237,0.2)' : 'transparent',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'all 0.15s ease', cursor: 'pointer',
                          boxShadow: allSelected ? '0 0 6px rgba(124,58,237,0.4)' : 'none',
                        }}>
                        {allSelected && <svg width="11" height="9" viewBox="0 0 11 9" fill="none"><path d="M1 4L4 7.5L10 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                        {someSelected && <div style={{ width: 8, height: 2, background: '#7c3aed', borderRadius: 1 }} />}
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 600, color: someSelected || allSelected ? '#a78bfa' : muted, whiteSpace: 'nowrap' }}>
                        {allSelected ? `All ${visibleTickets.length} selected` : someSelected ? `${selectedCount} of ${visibleTickets.length}` : `Select all`}
                      </span>
                    </label>
                  );
                })()}

                {/* Search by ID */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 12, color: muted }}>🔍 #</span>
                  <input value={searchId} onChange={e => setSearchId(e.target.value)} placeholder="Ticket ID"
                    style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${border}`, background: bg, color: text, fontSize: 12, width: 90 }} />
                </div>
                <div style={{ width: 1, height: 18, background: border }} />
                {/* Priority */}
                <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)}
                  style={{ padding: '5px 8px', borderRadius: 6, border: `1px solid ${border}`, background: bg, color: text, fontSize: 12 }}>
                  <option value="all">All priorities</option>
                  {['urgent','high','normal','low'].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                {/* Status */}
                {uniqueStatuses.length > 0 && (
                  <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                    style={{ padding: '5px 8px', borderRadius: 6, border: `1px solid ${border}`, background: bg, color: text, fontSize: 12 }}>
                    <option value="all">All statuses</option>
                    {uniqueStatuses.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                )}
                {/* Channel */}
                {uniqueChannels.length > 0 && (
                  <select value={filterChannel} onChange={e => setFilterChannel(e.target.value)}
                    style={{ padding: '5px 8px', borderRadius: 6, border: `1px solid ${border}`, background: bg, color: text, fontSize: 12 }}>
                    <option value="all">All channels</option>
                    {uniqueChannels.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                )}
                {/* Min comments filter */}
                <select value={minComments} onChange={e => setMinComments(Number(e.target.value))}
                  style={{ padding: '5px 8px', borderRadius: 6, border: `1px solid ${border}`, background: bg, color: text, fontSize: 12 }}>
                  <option value={0}>Any comments</option>
                  <option value={1}>≥ 1 comment</option>
                  <option value={2}>≥ 2 comments</option>
                  <option value={3}>≥ 3 comments</option>
                  <option value={5}>≥ 5 comments</option>
                </select>
                {/* Benchmarked filter */}
                <select value={filterWinner} onChange={e => setFilterWinner(e.target.value)}
                  style={{ padding: '5px 8px', borderRadius: 6, border: `1px solid ${border}`, background: bg, color: text, fontSize: 12 }}>
                  <option value="all">All results</option>
                  <option value="ai">🤖 AI Wins</option>
                  <option value="human">👤 Human Wins</option>
                  <option value="tied">🤝 Tied / AI Only</option>
                </select>
                {hasActiveFilters && (
                  <button onClick={clearFilters} style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, cursor: 'pointer', border: '1px solid rgba(239,68,68,0.3)', background: 'transparent', color: '#ef4444' }}>✕ Clear</button>
                )}
                <span style={{ marginLeft: 'auto', fontSize: 12, color: muted }}>{visibleTickets.length} of {importedTickets.length} tickets</span>
                <button onClick={loadTickets} disabled={loadingTickets} title="Reload tickets from backend" style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${border}`, background: 'transparent', color: muted, fontSize: 12, cursor: 'pointer' }}>🔄 {loadingTickets ? '...' : 'Refresh'}</button>
              </div>

              {/* Tag multi-select */}
              {allTags.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, color: muted, marginBottom: 6, fontWeight: 600 }}>TAGS {selectedTags.size > 0 && <span style={{ color: '#a78bfa' }}>({selectedTags.size} selected)</span>}</div>
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                    {allTags.map(tag => (
                      <button key={tag} onClick={() => toggleTag(tag)} style={{
                        padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 500, cursor: 'pointer', border: '1px solid',
                        borderColor: selectedTags.has(tag) ? '#7c3aed' : 'rgba(124,58,237,0.25)',
                        background: selectedTags.has(tag) ? 'rgba(124,58,237,0.2)' : 'rgba(124,58,237,0.05)',
                        color: selectedTags.has(tag) ? '#a78bfa' : '#64748b',
                        transition: 'all 0.1s',
                      }}>{tag}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Ticket list */}
            {loadingTickets ? (
              <div style={{ textAlign: 'center', padding: 60, color: muted }}>Loading imported tickets...</div>
            ) : ticketLoadError ? (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
                <h3 style={{ color: '#ef4444', margin: '0 0 8px' }}>Could not load tickets</h3>
                <p style={{ color: muted, fontSize: 13, maxWidth: 420, margin: '0 auto 8px' }}>{ticketLoadError}</p>
                <p style={{ color: muted, fontSize: 12, maxWidth: 420, margin: '0 auto 16px' }}>Make sure the backend is running and tickets have been imported in the Zendesk Tickets tab.</p>
                <button onClick={loadTickets} style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>🔄 Retry</button>
              </div>
            ) : importedTickets.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
                <h3 style={{ color: text, margin: '0 0 8px' }}>No tickets imported yet</h3>
                <p style={{ color: muted, fontSize: 13, maxWidth: 380, margin: '0 auto 20px' }}>Go to the Zendesk Tickets tab and import tickets first. Or try the demo:</p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button onClick={loadTickets} style={{ padding: '9px 18px', borderRadius: 8, border: `1px solid ${border}`, background: 'transparent', color: muted, fontSize: 13, cursor: 'pointer' }}>🔄 Refresh</button>
                  <button onClick={runMockBenchmark} style={{ padding: '10px 22px', borderRadius: 8, border: `1px solid ${border}`, background: 'transparent', color: text, fontSize: 14, cursor: 'pointer' }}>🎭 Run Demo (5 mock tickets)</button>
                </div>
              </div>
            ) : visibleTickets.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: muted }}>No tickets match the current filters. <button onClick={clearFilters} style={{ background: 'none', border: 'none', color: '#a78bfa', cursor: 'pointer', fontSize: 13, textDecoration: 'underline' }}>Clear filters</button></div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {visibleTickets.map(ticket => {
                  const tid = String(ticket.zendesk_ticket_id || ticket.id || '');
                  return (
                    <TicketSelectCard
                      key={tid}
                      ticket={ticket}
                      ticketId={tid}
                      selected={selectedIds.has(tid)}
                      onToggle={toggleTicket}
                      result={resultByTicketId[tid] || null}
                      border={border} text={text} muted={muted} cardBg={cardBg}
                    />
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ══════════════════════════════════════════════════════════
            RESULTS VIEW — Summary stats + per-ticket result cards
            ══════════════════════════════════════════════════════════ */}
        {viewMode === 'results' && (
          <>
            {!benchmarkData && !loadingExisting && (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>⚖️</div>
                <h3 style={{ color: text }}>No results yet</h3>
                <p style={{ color: muted, fontSize: 13 }}>Switch to Select Tickets and run a benchmark.</p>
                <button onClick={() => setViewMode('select')} style={{ marginTop: 12, padding: '9px 20px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', color: 'white', fontSize: 13, cursor: 'pointer' }}>🎯 Go to Select Tickets</button>
              </div>
            )}

            {summary && (() => {
              // ── Aggregate metrics computed from raw results ──────────────
              const allResults = benchmarkData.results || [];
              const withHuman  = allResults.filter(r => r.human?.score !== null && r.human?.score !== undefined && r.comparison?.winner !== 'ai_only');
              const metrics    = ['accuracy', 'tone', 'resolution', 'efficiency'];
              const avg = (arr, key) => arr.length ? Math.round(arr.reduce((s, r) => s + (r[key] || 0), 0) / arr.length * 10) / 10 : 0;
              const humanMetrics = { accuracy: avg(withHuman.map(r=>r.human), 'accuracy'), tone: avg(withHuman.map(r=>r.human), 'tone'), resolution: avg(withHuman.map(r=>r.human), 'resolution'), efficiency: avg(withHuman.map(r=>r.human), 'efficiency') };
              const aiMetrics    = { accuracy: avg(allResults.map(r=>r.ai), 'accuracy'),   tone: avg(allResults.map(r=>r.ai), 'tone'),   resolution: avg(allResults.map(r=>r.ai), 'resolution'),   efficiency: avg(allResults.map(r=>r.ai), 'efficiency')   };
              // Compute 0-100 overall scores locally from raw results so we are never
              // dependent on the backend summary which can include ai_only zeros in
              // the human average and produce misleading Human 0% / AI 100% display.
              const localHumanScore = withHuman.length
                ? Math.round(withHuman.reduce((s, r) => s + (r.human?.score || 0), 0) / withHuman.length)
                : null;
              const localAiScore = allResults.length
                ? Math.round(allResults.reduce((s, r) => s + (r.ai?.score || 0), 0) / allResults.length)
                : null;
              // Groundedness — only if this was a KB-grounded run
              const isKbRun = benchmarkData.kb_grounded || summary.kb_grounded || allResults.some(r => r.ai?.kb_grounded);
              const groundedResults = allResults.filter(r => r.ai?.groundedness != null);
              const avgGroundedness = groundedResults.length
                ? Math.round(groundedResults.reduce((s, r) => s + r.ai.groundedness, 0) / groundedResults.length * 10) / 10
                : null;
              const radarData = [
                ...metrics.map(m => ({ metric: m.charAt(0).toUpperCase()+m.slice(1), human: humanMetrics[m], ai: aiMetrics[m] })),
                ...(isKbRun && avgGroundedness != null ? [{ metric: 'Groundedness', human: 0, ai: avgGroundedness }] : []),
              ];
              const barData = metrics.map(m => ({ name: m.charAt(0).toUpperCase()+m.slice(1), Human: humanMetrics[m], AI: aiMetrics[m] }));
              // Compute wins locally from raw results — only count tickets that
              // have actual human data so ai_only tickets don't skew the comparison.
              const comparedResults = allResults.filter(r => r.comparison?.winner !== 'ai_only');
              const localAiWins    = comparedResults.filter(r => r.comparison?.winner === 'ai').length;
              const localHumanWins = comparedResults.filter(r => r.comparison?.winner === 'human').length;
              const localEvaluated = comparedResults.length || 1;
              const aiPct        = Math.round((localAiWins    / localEvaluated) * 100);
              const humanPct     = Math.round((localHumanWins / localEvaluated) * 100);
              const tiedPct      = 100 - aiPct - humanPct;
              const overallWinner = localAiWins > localHumanWins ? 'ai' : localHumanWins > localAiWins ? 'human' : 'tied';
              // Outcome distribution
              const outcomeCounts = { resolved: 0, escalated: 0, waiting_customer: 0, waiting_internal: 0, unresolved: 0, no_response: 0 };
              allResults.forEach(r => { const o = r.ai?.outcome || 'unresolved'; outcomeCounts[o] = (outcomeCounts[o] || 0) + 1; });
              const outcomeData  = Object.entries(outcomeCounts).filter(([,v])=>v>0).map(([k,v]) => ({ name: OUTCOME_CONFIG[k]?.label || k, value: v, color: OUTCOME_CONFIG[k]?.color || '#64748b', pct: Math.round(v/allResults.length*100) }));
              // Priority breakdown
              const byPriority = {};
              allResults.forEach(r => {
                const p = r.priority || 'unknown';
                if (!byPriority[p]) byPriority[p] = { ai: [], human: [] };
                byPriority[p].ai.push(r.ai?.score || 0);
                if (r.comparison?.winner !== 'ai_only') byPriority[p].human.push(r.human?.score || 0);
              });
              const priorityData = Object.entries(byPriority).map(([p, d]) => ({
                name: p, AI: Math.round(d.ai.reduce((a,b)=>a+b,0)/Math.max(d.ai.length,1)),
                Human: d.human.length ? Math.round(d.human.reduce((a,b)=>a+b,0)/d.human.length) : null, count: d.ai.length
              }));

              return (
              <>
                {/* ── AGGREGATE ANALYSIS PANEL ─────────────────────────── */}
                <div style={{ background: cardBg, border: `1px solid ${border}`, borderRadius: 16, overflow: 'hidden', marginBottom: 24 }}>

                  {/* Panel header — Overall Winner as focal point */}
                  <div style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.15), rgba(79,70,229,0.08))', borderBottom: `1px solid ${border}`, padding: '24px 28px' }}>
                    {/* Winner hero — centred, dominant */}
                    <div style={{ textAlign: 'center', marginBottom: 20 }}>
                      <div style={{ fontSize: 11, color: '#a78bfa', fontWeight: 700, letterSpacing: '0.10em', marginBottom: 10, textTransform: 'uppercase' }}>Overall Winner</div>
                      <div style={{
                        display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
                        padding: '16px 40px', borderRadius: 16,
                        border: `2px solid ${overallWinner==='ai'?'rgba(167,139,250,0.6)':overallWinner==='human'?'rgba(56,189,248,0.6)':'rgba(245,158,11,0.6)'}`,
                        background: overallWinner==='ai'?'rgba(139,92,246,0.12)':overallWinner==='human'?'rgba(56,189,248,0.12)':'rgba(245,158,11,0.12)',
                        boxShadow: `0 0 32px ${overallWinner==='ai'?'rgba(167,139,250,0.15)':overallWinner==='human'?'rgba(56,189,248,0.15)':'rgba(245,158,11,0.15)'}`,
                        animation: 'countUp 0.6s ease-out forwards',
                      }}>
                        <div style={{ fontSize: 36, marginBottom: 6 }}>
                          {overallWinner==='ai'?'🤖':overallWinner==='human'?'👤':'🤝'}
                        </div>
                        <div style={{ fontSize: 26, fontWeight: 900, color: overallWinner==='ai'?'#a78bfa':overallWinner==='human'?'#38bdf8':'#f59e0b', lineHeight: 1.1, marginBottom: 6 }}>
                          {overallWinner==='ai'?'AI Agent':overallWinner==='human'?'Human Agents':'Tied'}
                        </div>
                        <div style={{ fontSize: 13, color: muted, fontWeight: 500 }}>
                          {overallWinner==='ai'
                            ? `+${summary.score_gap}pts average advantage over humans`
                            : overallWinner==='human'
                            ? `+${Math.abs(summary.score_gap)}pts average advantage over AI`
                            : 'Within margin of error — effectively equal'}
                        </div>
                      </div>
                    </div>
                    {/* Sub-line: run summary */}
                    <div style={{ textAlign: 'center', fontSize: 13, color: muted }}>
                      {summary.evaluated} ticket{summary.evaluated!==1?'s':''} evaluated
                      {' · '}{withHuman.length} with human comparison
                      {' · '}{summary.ai_only||0} AI only
                    </div>
                  </div>

                  <div style={{ padding: 24 }}>

                    {/* ── Colorful stat cards row ─────────────────────── */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12, marginBottom: 24 }}>
                      {[
                        { label: 'Tickets Evaluated', value: summary.evaluated, sub: `${withHuman.length} with human data`, icon: '📋', g: 'linear-gradient(135deg,#4f46e5,#7c3aed)' },
                        { label: 'AI Wins',      value: `${aiPct}%`,    sub: `${localAiWins} ticket${localAiWins!==1?'s':''}`,    icon: '🤖', g: 'linear-gradient(135deg,#7c3aed,#a78bfa)' },
                        { label: 'Human Wins',   value: `${humanPct}%`, sub: `${localHumanWins} ticket${localHumanWins!==1?'s':''}`, icon: '👤', g: 'linear-gradient(135deg,#0369a1,#38bdf8)' },
                        { label: 'AI Avg Score', value: `${localAiScore ?? summary.avg_ai_score}%`,    sub: `Human avg ${localHumanScore !== null ? localHumanScore : (withHuman.length === 0 ? 'N/A' : summary.avg_human_score)}%`, icon: '📊', g: 'linear-gradient(135deg,#059669,#10b981)' },
                        { label: 'Score Gap',    value: `${summary.score_gap>0?'+':''}${summary.score_gap}pts`, sub: summary.score_gap>0?'AI advantage':'Human advantage', icon: summary.score_gap>=0?'📈':'📉', g: summary.score_gap>=0?'linear-gradient(135deg,#b45309,#f59e0b)':'linear-gradient(135deg,#0369a1,#38bdf8)' },
                      ].map(c => (
                        <div key={c.label} style={{ background: c.g, borderRadius: 12, padding: '14px 16px', position: 'relative', overflow: 'hidden' }}>
                          <div style={{ position: 'absolute', top: -16, right: -12, fontSize: 36, opacity: 0.15, lineHeight: 1 }}>{c.icon}</div>
                          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.75)', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 6, textTransform: 'uppercase' }}>{c.label}</div>
                          <div style={{ fontSize: 24, fontWeight: 900, color: 'white', lineHeight: 1, marginBottom: 4 }}>{c.value}</div>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)' }}>{c.sub}</div>
                        </div>
                      ))}
                    </div>

                    {/* ── KPI Summary Cards + ROI Projection ─────────── */}
                    {(() => {
                      // Containment: prefer outcome=resolved; fall back to AI win rate when
                      // outcomes are not resolved (e.g. demo/mock runs that always escalate).
                      const resolvedCount = allResults.filter(r => r.ai?.outcome === 'resolved').length;
                      const aiContainmentRate = resolvedCount > 0
                        ? Math.round(resolvedCount / Math.max(allResults.length, 1) * 100)
                        : aiPct; // fall back to score-based win rate as containment proxy
                      const avgAiTurns  = allResults.length ? Math.round(allResults.reduce((s,r) => s + (r.ai?.turns||0), 0) / allResults.length * 10) / 10 : 0;
                      const avgHumTurns = withHuman.length  ? Math.round(withHuman.reduce((s,r)  => s + (r.human?.turns||0), 0) / withHuman.length * 10) / 10 : 0;
                      const monthlyTickets  = monthlyVolInput > 0 ? monthlyVolInput : summary.evaluated * 20;
                      const humanCostPerRes = humanCostInput > 0 ? humanCostInput : 8;
                      // Outcome-based pricing: Zendesk charges per AI resolution.
                      // Net saving per deflected ticket = human cost minus Zendesk AI price.
                      const zdPricePerRes       = zdPriceInput > 0 ? zdPriceInput : 2;
                      const aiHandleRate        = aiContainmentRate / 100;
                      const netSavingPerTicket  = humanCostPerRes - zdPricePerRes;
                      const humanSavingsMonthly = Math.max(0, Math.round(monthlyTickets * aiHandleRate * netSavingPerTicket));
                      const annualSavings       = humanSavingsMonthly * 12;
                      const platformCost        = 299;
                      // ROI months starting from current month
                      const _now = new Date();
                      const _monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                      const roiMonths = Array.from({ length: 6 }, (_, i) => _monthNames[(_now.getMonth() + i) % 12]);
                      // Cumulative data — savings grows each month, cost is flat platform fee
                      const roiData = roiMonths.map((month, i) => ({
                        month,
                        savings: Math.round(humanSavingsMonthly * (i + 1)),
                        cost:    Math.round(platformCost * (i + 1)),
                      }));
                      const breakEvenMonth = roiData.findIndex(d => d.savings >= d.cost);
                      // Y-axis domain: show both lines by capping at max of both series
                      const roiMaxSavings = Math.max(...roiData.map(d => d.savings));
                      const roiMaxCost    = Math.max(...roiData.map(d => d.cost));
                      const roiYMax       = Math.max(roiMaxSavings, roiMaxCost);
                      return (
                        <div style={{ marginBottom: 24 }}>
                          {/* KPI cards */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16 }}>
                            {[
                              {
                                label: 'Estimated Monthly Savings',
                                value: `$${humanSavingsMonthly.toLocaleString()}`,
                                sub: `$${annualSavings.toLocaleString()} annually`,
                                icon: '💰',
                                trend: `↑ ${aiContainmentRate}% of tickets deflected`,
                                trendColor: '#10b981',
                              },
                              {
                                label: 'AI Containment Rate',
                                value: `${aiContainmentRate}%`,
                                sub: `${allResults.filter(r => r.ai?.outcome === 'resolved').length} of ${allResults.length} resolved`,
                                icon: '⚡',
                                trend: aiContainmentRate >= 60 ? '↑ Above 60% target' : '↓ Below 60% target',
                                trendColor: aiContainmentRate >= 60 ? '#10b981' : '#f59e0b',
                              },
                              {
                                label: 'Avg Resolution Time',
                                value: `${avgAiTurns} turns`,
                                sub: `vs ${avgHumTurns} human turns`,
                                icon: '⏱',
                                trend: avgAiTurns < avgHumTurns
                                  ? `↑ ${Math.round((1 - avgAiTurns / Math.max(avgHumTurns, 1)) * 100)}% faster than human`
                                  : '~ Similar to human',
                                trendColor: avgAiTurns < avgHumTurns ? '#10b981' : '#94a3b8',
                              },
                            ].map(k => (
                              <div key={k.label} style={{ background: cardBg, border: `1px solid ${border}`, borderRadius: 12, padding: '16px 18px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                                  <div style={{ fontSize: 11, color: muted, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{k.label}</div>
                                  <span style={{ fontSize: 20 }}>{k.icon}</span>
                                </div>
                                <div style={{ fontSize: 28, fontWeight: 900, color: text, lineHeight: 1, marginBottom: 4 }}>{k.value}</div>
                                <div style={{ fontSize: 11, color: muted, marginBottom: 8 }}>{k.sub}</div>
                                <div style={{ fontSize: 11, fontWeight: 600, color: k.trendColor }}>{k.trend}</div>
                              </div>
                            ))}
                          </div>

                          {/* ROI Projection chart */}
                          <div style={{ background: cardBg, border: `1px solid ${border}`, borderRadius: 12, padding: '18px 20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: text }}>💵 ROI Projection</div>
                                <div style={{ fontSize: 11, color: muted, marginTop: 2 }}>
                                  {breakEvenMonth >= 0 ? `Break-even at month ${breakEvenMonth + 1} · ` : ''}
                                  ${humanCostPerRes} human − ${zdPricePerRes} AI = ${netSavingPerTicket.toFixed(2)}/ticket net saving · {monthlyTickets.toLocaleString()} tickets/mo
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                <div style={{ fontSize: 11, color: muted, display: 'flex', gap: 14 }}>
                                  <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#10b981', marginRight: 4 }}/>Savings</span>
                                  <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: 'rgba(239,68,68,0.5)', marginRight: 4 }}/>Platform Cost</span>
                                </div>
                                <button
                                  onClick={() => setShowRoiCalc(v => !v)}
                                  style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: `1px solid ${border}`, background: 'transparent', color: muted, cursor: 'pointer' }}>
                                  {showRoiCalc ? '▲ Hide' : '⚙ Customize'}
                                </button>
                              </div>
                            </div>

                            {/* ── Settings panel ── */}
                            {showRoiCalc && (
                              <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${border}`, borderRadius: 10, padding: 16, marginBottom: 16 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: muted, letterSpacing: '0.06em', marginBottom: 12 }}>ROI ASSUMPTIONS</div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
                                  <div>
                                    <label style={{ fontSize: 11, color: muted, display: 'block', marginBottom: 5 }}>
                                      Cost per human resolution ($)
                                    </label>
                                    <input
                                      type="number" min="0.5" step="0.5"
                                      value={humanCostInput}
                                      onChange={e => {
                                        const v = parseFloat(e.target.value) || 8;
                                        setHumanCostInput(v);
                                        localStorage.setItem('roi_humanCost', v);
                                      }}
                                      style={{ width: '100%', padding: '7px 10px', borderRadius: 7, border: `1px solid ${border}`, background: bg, color: text, fontSize: 13 }}
                                    />
                                    <div style={{ fontSize: 10, color: muted, marginTop: 4 }}>
                                      Industry avg: $6–$15 per ticket (agent time + overhead)
                                    </div>
                                  </div>
                                  <div>
                                    <label style={{ fontSize: 11, color: muted, display: 'block', marginBottom: 5 }}>
                                      Zendesk AI price per resolution ($)
                                    </label>
                                    <input
                                      type="number" min="0" step="0.5"
                                      value={zdPriceInput}
                                      onChange={e => {
                                        const v = parseFloat(e.target.value) || 2;
                                        setZdPriceInput(v);
                                        localStorage.setItem('roi_zdPrice', v);
                                      }}
                                      style={{ width: '100%', padding: '7px 10px', borderRadius: 7, border: `1px solid ${border}`, background: bg, color: text, fontSize: 13 }}
                                    />
                                    <div style={{ fontSize: 10, color: muted, marginTop: 4 }}>
                                      Zendesk outcome-based pricing — charged per AI resolution
                                    </div>
                                  </div>
                                  <div>
                                    <label style={{ fontSize: 11, color: muted, display: 'block', marginBottom: 5 }}>
                                      Monthly ticket volume (total)
                                    </label>
                                    <input
                                      type="number" min="0" step="100"
                                      value={monthlyVolInput || ''}
                                      placeholder={`Auto: ${summary.evaluated * 20} (sample × 20)`}
                                      onChange={e => {
                                        const v = parseInt(e.target.value) || 0;
                                        setMonthlyVolInput(v);
                                        localStorage.setItem('roi_monthlyVol', v);
                                      }}
                                      style={{ width: '100%', padding: '7px 10px', borderRadius: 7, border: `1px solid ${border}`, background: bg, color: text, fontSize: 13 }}
                                    />
                                    <div style={{ fontSize: 10, color: muted, marginTop: 4 }}>
                                      Leave blank to auto-estimate from sample size
                                    </div>
                                  </div>
                                </div>

                                {/* Calculation explanation */}
                                <div style={{ background: 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.15)', borderRadius: 8, padding: '12px 14px' }}>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: '#a78bfa', marginBottom: 8 }}>📐 How we calculate this</div>
                                  <div style={{ fontSize: 11, color: muted, lineHeight: 1.7 }}>
                                    <div><strong style={{ color: text }}>AI Containment Rate</strong> = % of tickets where AI outperformed human agents (score-based)</div>
                                    <div style={{ marginTop: 4 }}><strong style={{ color: text }}>Net saving per ticket</strong> = human cost per resolution − Zendesk AI price per resolution</div>
                                    <div style={{ marginTop: 4 }}><strong style={{ color: text }}>Monthly Savings</strong> = monthly volume × containment rate × net saving per ticket</div>
                                    <div style={{ marginTop: 4 }}><strong style={{ color: text }}>ROI %</strong> = annual savings ÷ annual platform cost × 100</div>
                                    <div style={{ marginTop: 8, padding: '6px 10px', background: 'rgba(245,158,11,0.08)', borderRadius: 6, border: '1px solid rgba(245,158,11,0.2)', color: '#f59e0b' }}>
                                      ⚠ These are projections based on your benchmark sample. Actual results depend on real ticket distribution and AI configuration in production.
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                            <ResponsiveContainer width="100%" height={160}>
                              <AreaChart data={roiData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                                <defs>
                                  <linearGradient id="savingsGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%"  stopColor="#10b981" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.02} />
                                  </linearGradient>
                                  <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%"  stopColor="#ef4444" stopOpacity={0.35} />
                                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0.05} />
                                  </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                                <XAxis dataKey="month" tick={{ fontSize: 11, fill: muted }} axisLine={false} tickLine={false} />
                                <YAxis domain={[0, roiYMax * 1.05]} tick={{ fontSize: 10, fill: muted }} axisLine={false} tickLine={false}
                                  tickFormatter={v => v >= 1000 ? `$${(v/1000).toFixed(0)}k` : `$${v}`} width={44} />
                                <Tooltip
                                  contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, fontSize: 12 }}
                                  labelStyle={{ color: '#ffffff', fontWeight: 700, marginBottom: 4 }}
                                  formatter={(value, name) => [
                                    `$${value.toLocaleString()}`,
                                    name === 'savings' ? 'Cumulative Savings' : 'Platform Cost'
                                  ]}
                                  itemStyle={{ color: muted }}
                                />
                                <Area type="monotone" dataKey="cost"    stroke="#ef4444" strokeWidth={2} strokeDasharray="5 4" fill="url(#costGrad)"    dot={false} activeDot={{ r: 4, fill: '#ef4444' }} />
                                <Area type="monotone" dataKey="savings" stroke="#10b981" strokeWidth={2.5} fill="url(#savingsGrad)" dot={false} activeDot={{ r: 5, fill: '#10b981' }} />
                              </AreaChart>
                            </ResponsiveContainer>
                            <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(16,185,129,0.08)', borderRadius: 8, border: '1px solid rgba(16,185,129,0.2)', fontSize: 12, color: '#10b981', fontWeight: 600 }}>
                              📈 Annual ROI: {humanSavingsMonthly > platformCost
                                ? `${Math.round(annualSavings / (platformCost * 12) * 100)}% return on platform investment`
                                : 'Increase ticket volume to improve ROI'}
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                  {/* ── Row 1: Score rings + radar + metric bars ─────── */}
                    <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr 1fr 1fr', gap: 16, marginBottom: 28, alignItems: 'start' }}>

                      {/* Overall score comparison */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 11, color: '#38bdf8', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 10 }}>👤 HUMAN AVG</div>
                          <ScoreRing score={localHumanScore !== null ? localHumanScore : summary.avg_human_score} color="#38bdf8" size={88} />
                          <div style={{ marginTop: 10 }}>
                            {metrics.map(m => (
                              <div key={m} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: `1px solid ${border}` }}>
                                <span style={{ fontSize: 11, color: muted, textTransform: 'capitalize' }}>{m}</span>
                                <span style={{ fontSize: 11, fontWeight: 600, color: humanMetrics[m]>=4?'#10b981':humanMetrics[m]>=3?'#f59e0b':'#ef4444' }}>{humanMetrics[m]}/5</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 11, color: '#a78bfa', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 10 }}>🤖 AI AVG</div>
                          <ScoreRing score={localAiScore !== null ? localAiScore : summary.avg_ai_score} color="#a78bfa" size={88} />
                          <div style={{ marginTop: 10 }}>
                            {metrics.map(m => (
                              <div key={m} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: `1px solid ${border}` }}>
                                <span style={{ fontSize: 11, color: muted, textTransform: 'capitalize' }}>{m}</span>
                                <span style={{ fontSize: 11, fontWeight: 600, color: aiMetrics[m]>=4?'#10b981':aiMetrics[m]>=3?'#f59e0b':'#ef4444' }}>{aiMetrics[m]}/5</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Human vs AI Performance Bar Chart */}
                      <div style={{ background: isDark?'rgba(15,23,42,0.6)':'#f8fafc', borderRadius: 12, padding: '16px 12px', border: `1px solid ${border}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: text }}>📊 Human vs AI Performance</span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 5, marginLeft: 'auto', fontSize: 11, color: muted }}>
                            <span style={{ width: 10, height: 10, borderRadius: 2, background: 'rgba(107,114,128,0.6)', display: 'inline-block' }} /> Human Agent
                            <span style={{ width: 10, height: 10, borderRadius: 2, background: '#a78bfa', display: 'inline-block', marginLeft: 6 }} /> AI Agent
                          </span>
                        </div>
                        <ResponsiveContainer width="100%" height={220}>
                          <BarChart data={barData} barCategoryGap="25%" barGap={3} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                            <XAxis dataKey="name" tick={{ fontSize: 12, fill: muted }} axisLine={false} tickLine={false} />
                            <YAxis domain={[0, 5]} ticks={[0,1,2,3,4,5]} tick={{ fontSize: 10, fill: muted }} axisLine={false} tickLine={false} />
                            <Tooltip
                              cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                              contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, fontSize: 12, padding: '8px 12px' }}
                              labelStyle={{ color: '#ffffff', fontWeight: 700, marginBottom: 6 }}
                              formatter={(value, name) => [value.toFixed(1), name === 'Human' ? '👤 Human' : '🤖 AI']}
                              itemStyle={{ color: muted }}
                            />
                            <Bar dataKey="Human" fill="rgba(107,114,128,0.55)" radius={[4,4,0,0]} />
                            <Bar dataKey="AI"    fill="#a78bfa"              radius={[4,4,0,0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Radar chart */}
                      <div style={{ background: isDark?'rgba(15,23,42,0.6)':'#f8fafc', borderRadius: 12, padding: '16px 12px', border: `1px solid ${border}` }}>
                        <div style={{ fontSize: 11, color: muted, fontWeight: 700, letterSpacing: '0.06em', marginBottom: 4, textAlign: 'center' }}>PERFORMANCE PROFILE</div>
                        <ResponsiveContainer width="100%" height={260}>
                          <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
                            <PolarGrid stroke={border} />
                            <PolarAngleAxis dataKey="metric" tick={{ fill: muted, fontSize: 11, fontWeight: 600 }} />
                            <PolarRadiusAxis domain={[0,5]} tick={false} axisLine={false} />
                            {withHuman.length > 0 && <Radar name="Human" dataKey="human" stroke="#38bdf8" fill="#38bdf8" fillOpacity={0.15} strokeWidth={2.5} />}
                            <Radar name="AI" dataKey="ai" stroke="#a78bfa" fill="#a78bfa" fillOpacity={0.2} strokeWidth={2.5} />
                            <Tooltip formatter={(v, n) => [`${v}/5`, n==='human'?'Human Avg':'AI Avg']} contentStyle={{ background: cardBg, border: `1px solid ${border}`, borderRadius: 8 }} />
                            <Legend formatter={v => v==='human'?'👤 Human':'🤖 AI'} wrapperStyle={{ fontSize: 12 }} />
                          </RadarChart>
                        </ResponsiveContainer>
                        <div style={{ borderTop: `1px solid ${border}`, paddingTop: 12, marginTop: 4 }}>
                          {metrics.map(m => {
                            const diff = aiMetrics[m] - humanMetrics[m];
                            const winner = diff > 0.2 ? 'ai' : diff < -0.2 ? 'human' : 'tied';
                            return (
                              <div key={m} style={{ display: 'grid', gridTemplateColumns: '1fr 36px 1fr', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                                  <div style={{ height: 6, borderRadius: 3, width: `${humanMetrics[m]/5*100}%`, background: winner==='human'?'#38bdf8':border, minWidth: 4 }} />
                                  <span style={{ fontSize: 11, fontWeight: 700, color: winner==='human'?'#38bdf8':muted, whiteSpace:'nowrap' }}>{humanMetrics[m]}</span>
                                </div>
                                <div style={{ textAlign:'center', fontSize:10, color:muted, fontWeight:600, textTransform:'capitalize' }}>{m.slice(0,3)}</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span style={{ fontSize: 11, fontWeight: 700, color: winner==='ai'?'#a78bfa':muted, whiteSpace:'nowrap' }}>{aiMetrics[m]}</span>
                                  <div style={{ height: 6, borderRadius: 3, width: `${aiMetrics[m]/5*100}%`, background: winner==='ai'?'#a78bfa':border, minWidth: 4 }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Win distribution + outcome breakdown */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                        {/* Win distribution bar */}
                        <div style={{ background: isDark?'rgba(15,23,42,0.6)':'#f8fafc', borderRadius: 12, padding: 16, border: `1px solid ${border}` }}>
                          <div style={{ fontSize: 11, color: muted, fontWeight: 700, letterSpacing: '0.06em', marginBottom: 12 }}>WIN DISTRIBUTION</div>
                          <div style={{ display: 'flex', height: 28, borderRadius: 6, overflow: 'hidden', marginBottom: 10 }}>
                            {humanPct > 0 && <div style={{ width: `${humanPct}%`, background: '#38bdf8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'white', minWidth: 30 }}>{humanPct}%</div>}
                            {tiedPct > 0  && <div style={{ width: `${tiedPct}%`,  background: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'white', minWidth: 24 }}>{tiedPct}%</div>}
                            {aiPct > 0    && <div style={{ width: `${aiPct}%`,    background: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'white', minWidth: 30 }}>{aiPct}%</div>}
                          </div>
                          <div style={{ display: 'flex', gap: 14, justifyContent: 'center' }}>
                            {[['#38bdf8','👤 Human',`${localHumanWins} tickets`],['#f59e0b','🤝 Tied',`${comparedResults.length-localAiWins-localHumanWins} tickets`],['#7c3aed','🤖 AI',`${localAiWins} tickets`]].map(([c,l,s])=>(
                              <div key={l} style={{ textAlign:'center' }}>
                                <div style={{ display:'flex', alignItems:'center', gap:4, fontSize:11, color:c, fontWeight:700 }}><div style={{width:8,height:8,borderRadius:'50%',background:c}}/>{l}</div>
                                <div style={{ fontSize:10, color:muted }}>{s}</div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* AI outcome breakdown */}
                        <div style={{ background: isDark?'rgba(15,23,42,0.6)':'#f8fafc', borderRadius: 12, padding: 16, border: `1px solid ${border}`, flex: 1 }}>
                          <div style={{ fontSize: 11, color: muted, fontWeight: 700, letterSpacing: '0.06em', marginBottom: 12 }}>AI OUTCOME DISTRIBUTION</div>
                          {outcomeData.map(o => (
                            <div key={o.name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                              <div style={{ width: 8, height: 8, borderRadius: '50%', background: o.color, flexShrink: 0 }} />
                              <div style={{ fontSize: 12, color: text, minWidth: 130 }}>{o.name}</div>
                              <div style={{ flex: 1, height: 6, background: border, borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${o.pct}%`, background: o.color, borderRadius: 3, transition: 'width 0.5s ease' }} />
                              </div>
                              <div style={{ fontSize: 11, color: muted, width: 36, textAlign: 'right' }}>{o.pct}%</div>
                              <div style={{ fontSize: 11, color: muted, width: 16, textAlign: 'right' }}>{o.value}</div>
                            </div>
                          ))}
                        </div>

                        {/* Priority breakdown */}
                        {priorityData.length > 1 && (
                          <div style={{ background: isDark?'rgba(15,23,42,0.6)':'#f8fafc', borderRadius: 12, padding: 16, border: `1px solid ${border}` }}>
                            <div style={{ fontSize: 11, color: muted, fontWeight: 700, letterSpacing: '0.06em', marginBottom: 12 }}>SCORE BY PRIORITY</div>
                            <ResponsiveContainer width="100%" height={120}>
                              <BarChart data={priorityData} barCategoryGap="30%">
                                <CartesianGrid stroke={border} vertical={false} />
                                <XAxis dataKey="name" tick={{ fill: muted, fontSize: 11, textTransform:'capitalize' }} axisLine={false} tickLine={false} />
                                <YAxis domain={[0,100]} tick={{ fill: muted, fontSize: 10 }} axisLine={false} tickLine={false} width={28} />
                                <Tooltip contentStyle={{ background: cardBg, border: `1px solid ${border}`, borderRadius: 8, fontSize: 12 }} formatter={(v,n) => [v+'%', n]} />
                                <Bar dataKey="Human" fill="#38bdf8" radius={[4,4,0,0]} />
                                <Bar dataKey="AI"    fill="#a78bfa" radius={[4,4,0,0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ── Row 2: Key insight + recommendations ────────── */}
                    <div style={{ display: 'grid', gridTemplateColumns: summary.key_insight ? '1fr 1fr' : '1fr', gap: 16, borderTop: `1px solid ${border}`, paddingTop: 20 }}>
                      {summary.key_insight && (
                        <div style={{ background: 'linear-gradient(135deg,rgba(124,58,237,0.08),rgba(79,70,229,0.04))', border: '1px solid rgba(124,58,237,0.25)', borderRadius: 12, padding: '14px 18px' }}>
                          <div style={{ fontSize: 11, color: '#a78bfa', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 8 }}>💡 KEY INSIGHT</div>
                          <div style={{ fontSize: 14, color: text, lineHeight: 1.7 }}>{summary.key_insight}</div>
                        </div>
                      )}
                      {/* Auto-generated recommendations */}
                      <div style={{ background: isDark?'rgba(15,23,42,0.4)':'#f8fafc', border: `1px solid ${border}`, borderRadius: 12, padding: '14px 18px' }}>
                        <div style={{ fontSize: 11, color: '#10b981', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 8 }}>🎯 RECOMMENDATIONS</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {(() => {
                            const recs = [];
                            const lowestMetric = metrics.reduce((a,b) => aiMetrics[a]<aiMetrics[b]?a:b);
                            const aiContainment = outcomeCounts.resolved / Math.max(allResults.length,1);
                            if (aiMetrics[lowestMetric] < 4) recs.push({ icon: '📈', text: `Focus on improving AI ${lowestMetric} — currently ${aiMetrics[lowestMetric]}/5, the weakest metric` });
                            if (aiContainment < 0.7) recs.push({ icon: '🔄', text: `AI resolves ${Math.round(aiContainment*100)}% of tickets — review escalation patterns in failed tickets` });
                            if (localAiWins / Math.max(localEvaluated,1) > 0.8) recs.push({ icon: '🚀', text: `AI outperforms humans on ${aiPct}% of tickets — strong candidate for production deployment` });
                            if (withHuman.length < allResults.length * 0.5) recs.push({ icon: '💬', text: `${summary.ai_only||0} tickets had no human response — ensure comments are imported to enable full comparison` });
                            const lowestHuman = metrics.reduce((a,b) => humanMetrics[a]<humanMetrics[b]?a:b);
                            if (humanMetrics[lowestHuman] < 3.5) recs.push({ icon: '🧑‍💼', text: `Human ${lowestHuman} scored ${humanMetrics[lowestHuman]}/5 — consider agent coaching in this area` });
                            if (recs.length === 0) recs.push({ icon: '✅', text: 'Both human and AI performance looks solid. Consider expanding the test to more ticket types.' });
                            return recs.slice(0, 4).map((r, i) => (
                              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                                <span style={{ fontSize: 14, flexShrink: 0 }}>{r.icon}</span>
                                <span style={{ fontSize: 13, color: text, lineHeight: 1.5 }}>{r.text}</span>
                              </div>
                            ));
                          })()}
                        </div>
                      </div>
                    </div>

                  </div>
                </div>

                {/* ── PER-TICKET RESULTS ───────────────────────────── */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: muted, letterSpacing: '0.06em' }}>PER-TICKET RESULTS</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <input value={searchId} onChange={e => setSearchId(e.target.value)} placeholder="🔍 Ticket #"
                      style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${border}`, background: bg, color: text, fontSize: 12, width: 100 }} />
                    {[['all','All'],['ai','🤖 AI'],['human','👤 Human'],['tied','🤝 Tied']].map(([v,l]) => (
                      <button key={v} onClick={() => setFilterWinner(v)} style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: '1px solid', borderColor: filterWinner===v?'#7c3aed':border, background: filterWinner===v?'rgba(124,58,237,0.15)':'transparent', color: filterWinner===v?'#a78bfa':muted }}>{l}</button>
                    ))}
                    <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} style={{ padding: '4px 8px', borderRadius: 6, border: `1px solid ${border}`, background: bg, color: text, fontSize: 12 }}>
                      <option value="all">All priorities</option>
                      {['urgent','high','normal','low'].map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <span style={{ fontSize: 12, color: muted }}>{filteredResults.length} of {benchmarkData.results.length}</span>
                    {hasActiveFilters && <button onClick={clearFilters} style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, cursor: 'pointer', border: '1px solid rgba(239,68,68,0.3)', background: 'transparent', color: '#ef4444' }}>✕ Clear</button>}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {(() => {
                    const included = filteredResults.filter(t => t.comparison?.winner !== 'ai_only');
                    const ignored  = filteredResults.filter(t => t.comparison?.winner === 'ai_only');
                    return (
                      <>
                        {included.map(t => <ResultCard key={t.ticket_id} ticket={t} border={border} text={text} muted={muted} cardBg={cardBg} />)}
                        {ignored.length > 0 && (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '8px 0 4px' }}>
                              <div style={{ flex: 1, height: 1, background: border }} />
                              <span style={{ fontSize: 11, fontWeight: 600, color: muted, letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
                                {ignored.length} AI-ONLY — NO HUMAN DATA
                              </span>
                              <div style={{ flex: 1, height: 1, background: border }} />
                            </div>
                            {ignored.map(t => <ResultCard key={t.ticket_id} ticket={t} border={border} text={text} muted={muted} cardBg={cardBg} />)}
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>
                {filteredResults.length === 0 && <div style={{ textAlign: 'center', padding: 40, color: muted }}>No results match the current filters.</div>}
              </>
              );
            })()}
          </>
        )}

      </div>

        {/* ══════════════════════════════════════════════════════════
            A/B RESULTS VIEW
            ══════════════════════════════════════════════════════════ */}
        {viewMode === 'ab_results' && abData && (
          <ABResultsView
            data={abData}
            theme={theme}
            border={border}
            text={text}
            muted={muted}
            cardBg={cardBg}
            bg={bg}
          />
        )}

      {/* ── KB / Prompt config modal — shown before running benchmark ── */}
      <RunTestModal
        isOpen={benchmarkModalOpen}
        onClose={() => setBenchmarkModalOpen(false)}
        onConfirm={(config) => executeOnSelected(config)}
        mode="batch"
        ticketSubject={`${[...selectedIds].length} ticket${[...selectedIds].length !== 1 ? 's' : ''} selected`}
      />

    </div>
  );
}
